// Framework-agnostic canvas engine for building/editing GPO track JSON files, driven by the
// website's /track-editor view (TrackEditorView.vue) — the game repo has no editor of its own.
// The file format is version 2 (see trackFormat.js): PIECES of road, each a chain of nodes with
// their Bezier handles and a width per node. It is what the game server loads from
// gpo2/src/assets/tracks/*.json, and what the track editor writes.
import { samplePiece } from './trackGeometry.js';
import { drawStand } from './standLayout.js';
import { drawFence } from './fenceLayout.js';
import { DEFAULT_MATERIAL, MATERIALS, materialsForType, materialVariant, variantRatio } from './materials.js';
import { compareTyreDepth, drawTyreCell, tyreSet, tyreStampsForObstacle } from './tyreLayout.js';
import { createTrackCanvas, materialImages, scaleWithinCeilings, terrainImages, trackExportStamp } from './trackPainter.js';
import { DEFAULT_SURFACE, DEFAULT_TERRAIN, SURFACE_STYLES, TERRAIN_STYLES, buildPieces, fromTrackFile, surfaceOf, terrainOf, toTrackFile } from './trackFormat.js';

const HR = 7;       // handle radius in screen pixels
const CP_CLICK = 8; // click tolerance for checkpoint lines (screen px)
const SNAP_R = 20;  // magnetic snap radius in screen pixels
// The road's own edge/centre line, which the magnet also catches (see nearestOnRoad): tighter than
// SNAP_R on purpose, since the road is a continuous curve and a node is a single point — the same
// radius would make the road win over everything the moment you are anywhere near the asphalt.
const ROAD_SNAP_R = 14;
// Distance (screen px) within which a point lines up with another one: within it the editor draws
// the alignment line and — magnet on — pulls the point onto that line.
const GUIDE_R = 9;  // alignment-line radius, in screen pixels
// Sample step for a Bezier edge (world units): the shape the game reads is this polyline, and the
// painter draws it segment by segment, so this IS the flatness of the curve on screen — a 24-unit
// step reads as a chain of 40-px straight runs with a corner in between the moment the editor is
// zoomed in. Curved edges are a small part of a track's perimeter and straight ones cost two points
// whatever this is, so the finer step is cheap; MAX is the guard for a very long curved edge.
const EDGE_STEP = 6;
const EDGE_STEP_MAX = 256;
// Handles closer than this to their own node are treated as absent (a 0-length handle has no
// direction, and it would stack two grab circles on the same pixel).
const HANDLE_MIN = 6;

// World units of margin the game area keeps around the track, on every side: what fitGameArea()
// sizes the area with, and the same value an export works an automatic size out with (see the end
// of this file).
const AREA_MARGIN = 400;

// The flat colours an "Export PNG" paints its three layers with — the road (its asphalt shapes) black,
// the objects red, the lines (starting grid and finish line) light blue. One colour each and no
// texture: they are what tells the layers apart, and each layer is written as its own file (see
// exportImages).
const LAYER_COLORS = { track: '#000000', objects: '#ff0000', lines: '#22d3ee' };
// How thick the lines of that third group are drawn, in world units (the road is around 180 across, so
// this is a thin marking rather than a band).
const LAYER_LINE_WIDTH = 12;
// Longest side of an exported image, in pixels: a track a few thousand units across comes out 1 world
// unit per pixel, and an enormous one is scaled down instead of asking the browser for a canvas it
// would refuse to allocate.
const LAYER_MAX_PX = 8192;

// The world size a wall's texture repeats at, in world units. MUST MATCH the tile the painter builds a
// textured wall from (see paintBarriers in ./trackPainter.js), or the editor would show a grain the game
// does not have.
const WALL_TILE = 192;

const STATUS_MESSAGES = {
  select: 'Select mode — click to select',
  addSeg: 'Add Segment — click first point, then second  (Esc to cancel)',
  addCp: 'Add Checkpoint — click first point, then second  (Esc to cancel)',
  addSg: 'Add Starting Grid — click first point, then second  (Esc to cancel)',
  addPit: 'Add Pit Box — click first corner, then opposite corner  (Esc to cancel)',
  addWall: 'Add Wall — click first point, then second  (Esc to cancel)',
  addCircle: 'Add Circle — click center, then edge  (Esc to cancel)',
  addRect: 'Add Rectangle — click first corner, then opposite corner  (Esc to cancel)',
  addPoly: 'Add Polygon — click to add points, double-click or Enter to finish  (Esc to cancel)',
  addRoad: 'Add Road Area — click around the road perimeter node by node, double-click or Enter to finish (it closes back on the first node)  (Esc to cancel)',
  addTerrainRect: 'Add Terrain Rectangle — click first corner, then opposite corner  (Esc to cancel)',
  addTerrainCircle: 'Add Terrain Circle — click center, then edge  (Esc to cancel)',
  addTerrainArea: 'Add Terrain Area — click around the ground perimeter node by node, double-click or Enter to finish (it closes back on the first node)  (Esc to cancel)',
};

// Obstacles (walls/circles/rects/polygons) are impassable and always drawn in a distinct color
// from checkpoints/segments so they read as "solid" in the editor.
const OBS_COLOR = '#dc2626';

// Terrain areas are GROUND, drawn as a translucent fill so the road and the background still read
// through them. One colour per terrain (the names come from trackFormat.TERRAIN_STYLES, kept in step
// with the game's simulation catalogue): a colour the map doesn't know falls back to the second entry.
const TERRAIN_COLORS = {
  grass: '#4f8a3a',
  grass_dirt: '#a9793f',
  fine_grass: '#4a7f33',
  cobblestone: '#8b8f98',
  cobblestone_grass_grid: '#7f8a6a',
  sand: '#c9ae7c',
  sand2: '#c2a878',
  rocky_sand: '#b7a780',
};
const terrainColor = (name) => TERRAIN_COLORS[name] ?? TERRAIN_COLORS.grass_dirt;

// What an obstacle is made of has to be a name the game can paint (see the painter's materials.js):
// anything else is stored as the default, instead of a name that would silently paint as something
// else entirely.
const knownMaterial = (name, type) => (materialsForType(type ?? 'wall').includes(name) ? name : DEFAULT_MATERIAL);

/**
 * The width / height this obstacle's material has to keep, or null when it isn't one sprite fitted to a
 * rectangle (`stands`, `houses` — see the catalogue in the painter's materials.js).
 *
 * Such a shape IS its sprite: while you drag it the two sides move together, and the sprite always lands
 * exactly. `variant` picks which sprite (the material's `variants` list).
 */
function spriteRatio(obstacle) {
  if (obstacle?.type !== 'rect') return null;
  if (!materialVariant(obstacle.material, obstacle.variant ?? 0)) return null;
  return variantRatio(obstacle.material, obstacle.variant ?? 0);
}

// Valid values for the pit box's `crewSide` (which edge of the box the game's pit crew walks out
// of), plus 'auto' = leave it to the game. See gpo2 client/rendering/pitCrew/crewLayout.js.
const PIT_CREW_SIDES = ['auto', 'left', 'right', 'top', 'bottom'];
// Where the crew appears when the pit box doesn't say: `crewOffset` px OUTSIDE the box's crew
// side. MUST MATCH clientConfig.PIT_CREW.SPAWN_OFFSET in the game client.
const DEFAULT_CREW_OFFSET = 24;

// Valid values for the pit box's `direction` — which way the cars parked in it face. 'auto' leaves
// it to the game's own rule (the lap direction, from the checkpoints); 'forward'/'backward' force
// the sense the pit lane next to the box is DRAWN in, or its opposite; a number is an angle in
// radians, for a box that not even those can settle. MUST MATCH boxFacing in the game's
// src/server/game/trackUtils.js — the panel shows the resulting facing, so the two have to agree.
const PIT_DIRECTIONS = ['auto', 'forward', 'backward'];
// How close the pit lane has to pass to the box to be "the lane the box sits on": the game's own
// BRANCH_MAX_DIST (see trackUtils.PIT_GRID), mirrored so the preview faces what the game parks.
const PIT_LANE_REACH = 500;

// Staggered F1-style start grid preview (mirrors gpo2's client/public/js/lib/gridUtils.js).
// Within each row, one column is advanced `staggerPx` (total) ahead of the other; `flip` swaps
// which column (even/odd grid position) leads.
function buildStartGrid(pos, angle, count, staggerPx = 20, flip = false) {
  const back = angle + Math.PI, perp = angle + Math.PI / 2;
  const ROW = 60, COL = 35;
  return Array.from({ length: count }, (_, i) => {
    const col = i % 2;
    const isLead = flip ? col === 1 : col === 0;
    const rowBack = Math.floor(i / 2) * ROW + (isLead ? -staggerPx / 2 : staggerPx / 2);
    return {
      x: Math.round(pos.x + Math.cos(back) * rowBack + Math.cos(perp) * (col === 0 ? -1 : 1) * COL),
      y: Math.round(pos.y + Math.sin(back) * rowBack + Math.sin(perp) * (col === 0 ? -1 : 1) * COL)
    };
  });
}

/**
 * A grid line for a start pose that has none (see startPose): the file carries the start in `grid`
 * alone now, so a track with no drawn line would otherwise lose it. The line spans the road width,
 * centred on the pose; its direction is the facing turned -90° — the same normal the game reads the
 * facing back out of.
 */
function gridForPose(pos, angle, roadWidth) {
  const half = (roadWidth || 180) / 2;
  const sx = Math.sin(angle) * half, sy = Math.cos(angle) * half;
  return {
    x1: pos.x - sx, y1: pos.y + sy,
    x2: pos.x + sx, y2: pos.y - sy,
    staggerPx: 20, flip: false,
  };
}

/** Crew offset in px: whole numbers within a sane range (0 = right on the border). */
function clampCrewOffset(px) {
  return Math.max(0, Math.min(400, Math.round(Number(px) || 0)));
}

/** A pit box direction as the engine keeps it: a known name, or an angle in radians. */
function knownPitDirection(value) {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  return PIT_DIRECTIONS.includes(value) ? value : 'auto';
}

/** An angle brought back into (-π, π], the range the game's own flip keeps to. */
function normAngle(a) {
  return ((a + Math.PI) % (Math.PI * 2) + Math.PI * 2) % (Math.PI * 2) - Math.PI;
}

/**
 * The direction the cars face, from a grid read out of a file: `angle` is the answer (that is what
 * the game reads). A file with no `angle` (an old export) meant the line's normal on its +90° side,
 * which is the same facing the editor gives a freshly drawn line.
 */
function gridFacing(grid) {
  if (Number.isFinite(grid?.angle)) return grid.angle;
  return Math.atan2(grid.y2 - grid.y1, grid.x2 - grid.x1) + Math.PI / 2;
}

/**
 * The segment of `runs` closest to `point`, as { d, angle }: the direction of that segment and how
 * far it is. `runs` are sampled pieces ({ points, closed }) and a closed one is walked all the way
 * round. Distances are to the segment itself, the way the game measures them.
 */
function nearestRunAngle(runs, point, maxDist = Infinity) {
  let best = null;
  for (const run of runs) {
    const points = run.points;
    const last = run.closed ? points.length : points.length - 1;
    for (let i = 0; i < last; i++) {
      const a = points[i], b = points[(i + 1) % points.length];
      const dx = b.x - a.x, dy = b.y - a.y;
      const lengthSq = dx * dx + dy * dy;
      if (lengthSq < 0.0001) continue;
      const t = Math.max(0, Math.min(1, ((point.x - a.x) * dx + (point.y - a.y) * dy) / lengthSq));
      const d = Math.hypot(point.x - (a.x + dx * t), point.y - (a.y + dy * t));
      if (d <= maxDist && (!best || d < best.d)) best = { d, angle: Math.atan2(dy, dx) };
    }
  }
  return best;
}

/**
 * The centre of the circle through three points, or `null` when they are in line. The test is
 * geometric rather than numeric — the SAGITTA, how far the middle point sits off the line joining the
 * other two, in world units — because the nodes can be a thousand units apart or one unit apart and
 * the same tolerance has to mean the same thing in both cases.
 */
function circleThrough(a, b, c, tolerance) {
  const det = 2 * (a.x * (b.y - c.y) + b.x * (c.y - a.y) + c.x * (a.y - b.y));
  const base = Math.hypot(c.x - a.x, c.y - a.y);
  if (!(base > 0) || Math.abs(det) / (2 * base) < tolerance) return null;
  const a2 = a.x * a.x + a.y * a.y;
  const b2 = b.x * b.x + b.y * b.y;
  const c2 = c.x * c.x + c.y * c.y;
  return {
    x: (a2 * (b.y - c.y) + b2 * (c.y - a.y) + c2 * (a.y - b.y)) / det,
    y: (a2 * (c.x - b.x) + b2 * (a.x - c.x) + c2 * (b.x - a.x)) / det,
  };
}

// History keeps editor geometry at full precision, including identities and Bezier handles.
// Camera, selection, clipboard and tool defaults do not consume undo steps.
const HISTORY_FIELDS = [
  'segs', 'roadAreas', 'terrainAreas', 'cps', 'sg', 'pit', 'obstacles',
  'nextSeg', 'nextArea', 'nextTerrain', 'nextCp', 'nextObs',
  'trackId', 'trackName', 'manualWidth', 'manualHeight',
];

export class TrackEditorEngine {
  constructor({ onChange, onCoords, onCursor, createCanvas } = {}) {
    this.onChange = onChange || (() => {});
    this.onCoords = onCoords || (() => {});
    this.onCursor = onCursor || (() => {});

    this.canvas = null;
    this.ctx = null;
    this.wrapper = null;
    this._resizeObserver = null;

    this.segs = [];
    this.roadAreas = [];  // free road shapes: a CLOSED polygon whose interior is asphalt
    this.terrainAreas = []; // ground figures: CLOSED polygons, each with a `terrain` (see trackFormat.TERRAIN_STYLES)
    this.cps = [];
    this.obstacles = [];
    this.nextSeg = 1;
    this.nextArea = 1;
    this.nextTerrain = 1;
    this.nextCp = 0;
    this.nextObs = 1;

    this.mode = 'select';
    this.selected = null;
    this.selNode = null;            // index of the selected node in an area/poly (its handles show)
    this.hovered = null;
    this.drag = null;
    this.addFirst = null;
    this.draft = null;              // in-progress polygon/area points (addPoly, addRoad)
    this.draftKind = 'poly';        // which object type the draft becomes when finished
    this.extend = null;             // { id, atStart } when the draft appends to an existing polygon
    this.hudOverride = null;        // transient status text (e.g. "click to continue this polygon")
    this.guides = { x: null, y: null }; // alignment lines through the point being placed/dragged
    this.mw = { x: 0, y: 0 };
    this.clipboard = null;

    this.panX = 0; this.panY = 0; this.zoom = 0.15;
    this.panState = null;

    this.defWidth = 180;
    this.defLane = 'main';
    this.defObsThickness = 20;      // default wall thickness for new obstacles
    this.defObsRestitution = 0.15;  // default bounciness for new obstacles (kept low — these are walls)
    this.defObsFriction = 400;      // default px/s² deceleration while touching an obstacle
    this.defObsMaterial = 'wall';   // default impact-sound material for new obstacles ('wall'|'tyre')
    this.defPolyThickness = 8;      // default wall thickness for new polygons (thinner than a plain wall)
    this.defTerrain = 'grass_dirt'; // default terrain for new terrain figures (grass would change nothing)
    this.sg = null;
    this.pit = null;
    this.bgImage = null;
    // Decoded sprites, by src. The engine cannot fetch files (the game's painter has the same rule:
    // it takes `opts.images`), so the host loads what the engine asks for and hands it back — see
    // requestImage/setMaterialImages.
    this.materialImages = new Map();
    this.imagesRequested = new Set();
    // Wall textures built once per picture: a tiled pattern, reused while the view is panned/zoomed (the
    // pattern's own transform is re-anchored on every draw).
    this._wallTextures = new Map();
    this.tyreSets = new Map();
    this.onImages = null;
    // Offscreen canvases, used only to measure a tyre sprite (how much of its cell it fills). A host without
    // a DOM — the headless tests — passes its own, and the measurement falls back to the whole cell.
    this.createCanvas = createCanvas ?? (typeof document === 'undefined' ? null : (w, h) => {
      const canvas = document.createElement('canvas');
      canvas.width = w; canvas.height = h;
      return canvas;
    });
    this._boundsCache = null;
    this.magneticSnap = true;
    this.snapTarget = null;
    this.hideObjects = false; // when on, fades track/objects near-transparent to reveal the background
    this.busy = false; // an export holds the editor still (see setBusy)

    this.trackId = 'track_custom';
    this.trackName = 'Custom Track';
    this.manualWidth = null;
    this.manualHeight = null;

    this.historyLimit = 20;
    this.undoStack = [];
    this.redoStack = [];
    this.historyRevision = 0;
    this._historyDepth = 0;
    this._historyCurrent = this.captureHistory();
    this._historySignature = JSON.stringify(this._historyCurrent.document);

    this._onDown = this.onDown.bind(this);
    this._onMove = this.onMove.bind(this);
    this._onUp = this.onUp.bind(this);
    this._onLeave = () => {
      this.setHovered(null);
      this.onCursor(this.mode === 'select' ? 'default' : 'crosshair');
    };
    this._onWheel = this.onWheel.bind(this);
    this._onDblClick = this.onDblClick.bind(this);
    this._onContextMenu = (e) => {
      e.preventDefault();
      // Right-click finishes an in-progress polygon (the drawing is already under way, so
      // discarding it would be surprising); anywhere else it just cancels/deselects.
      const drafted = (this.draft?.length ?? 0) + (this.extend ? 1 : 0);
      if ((this.mode === 'addPoly' || this.mode === 'addRoad' || this.mode === 'addTerrainArea') && drafted >= 2) this.finishDraft();
      else this.cancel();
    };
    this._onKeydown = this.onKeydown.bind(this);
  }

  // ── Lifecycle ────────────────────────────────────────────────
  mount(canvas, wrapper) {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d');
    this.wrapper = wrapper;

    this._resizeObserver = new ResizeObserver(([entry]) => {
      canvas.width = entry.contentRect.width;
      canvas.height = entry.contentRect.height;
      this.render();
    });
    this._resizeObserver.observe(wrapper);

    canvas.addEventListener('mousedown', this._onDown);
    canvas.addEventListener('mousemove', this._onMove);
    canvas.addEventListener('mouseleave', this._onLeave);
    window.addEventListener('mouseup', this._onUp);
    canvas.addEventListener('wheel', this._onWheel, { passive: false });
    canvas.addEventListener('contextmenu', this._onContextMenu);
    canvas.addEventListener('dblclick', this._onDblClick);
    window.addEventListener('keydown', this._onKeydown);

    this.setMode('select');
  }

  destroy() {
    if (!this.canvas) return;
    this._resizeObserver?.disconnect();
    this.canvas.removeEventListener('mousedown', this._onDown);
    this.canvas.removeEventListener('mousemove', this._onMove);
    this.canvas.removeEventListener('mouseleave', this._onLeave);
    window.removeEventListener('mouseup', this._onUp);
    this.canvas.removeEventListener('wheel', this._onWheel);
    this.canvas.removeEventListener('contextmenu', this._onContextMenu);
    this.canvas.removeEventListener('dblclick', this._onDblClick);
    window.removeEventListener('keydown', this._onKeydown);
    this.canvas = null;
    this.ctx = null;
  }

  // ── Snapshot for the host UI ────────────────────────────────
  /** Lightweight object identities for the host's two-level scene list. */
  getObjectTree() {
    const item = (type, id, label) => ({ type, id, key: `${type}:${id}`, label });
    const obstacleNames = { wall: 'Wall', circle: 'Circle', rect: 'Rectangle', poly: 'Polygon' };
    return [
      { key: 'road', label: 'Road', color: '#60a5fa', children: [
        ...this.segs.map(s => item('seg', s.id, `Segment #${s.id}`)),
        ...this.roadAreas.map(a => item('road', a.id, `Road Area #${a.id}`)),
      ] },
      { key: 'logic', label: 'Race Logic', color: '#34d399', children: [
        ...this.cps.map(c => item('cp', c.id, `Checkpoint #${c.id}${this.isStartCp(c) ? ' (Start / Finish)' : ''}`)),
        ...(this.sg ? [item('sg', 'sg', 'Starting Grid')] : []),
        ...(this.pit ? [item('pit', 'pit', 'Pit Box')] : []),
      ] },
      { key: 'obstacles', label: 'Obstacles', color: '#dc2626', children:
        this.obstacles.map(o => item(o.type, o.id, `${obstacleNames[o.type] ?? o.type} #${o.id}`)),
      },
      { key: 'terrain', label: 'Terrain', color: '#a9793f', children:
        this.terrainAreas.map(a => item('terrain', a.id, `Terrain Area #${a.id}`)),
      },
    ];
  }

  /** Reuse the canvas hover rendering without changing selection or drawing mode. */
  setHovered(type, id) {
    const next = type && this.getObj(type, id) ? { type, id, handle: 'body' } : null;
    if (next?.type === this.hovered?.type && next?.id === this.hovered?.id && next?.handle === this.hovered?.handle) return;
    this.hovered = next;
    this.render();
  }

  selectObject(type, id) {
    if (!this.getObj(type, id)) return;
    const sameObject = this.selected?.type === type && this.selected?.id === id;
    this.setMode('select');
    if (!sameObject) this.selNode = null;
    this.setSelected(type, id);
  }

  getSnapshot() {
    let selection = null;
    const sel = this.selected;
    if (sel?.type === 'seg') {
      const s = this.getObj('seg', sel.id);
      selection = s && { type: 'seg', id: sel.id, width: s.width, lane: s.lane ?? 'main', surface: surfaceOf(s), hasCurve: s.cx != null };
    } else if (sel?.type === 'cp') {
      const c = this.getObj('cp', sel.id);
      selection = c && { type: 'cp', id: sel.id, isStart: this.isStartCp(c), timing: !!c.timing };
    } else if (sel?.type === 'sg' && this.sg) {
      selection = { type: 'sg', staggerPx: this.sg.staggerPx ?? 20, flip: !!this.sg.flip };
    } else if (sel?.type === 'pit' && this.pit) {
      selection = { type: 'pit', crewSide: this.pit.crewSide ?? 'auto', crewOffset: this.pit.crewOffset ?? DEFAULT_CREW_OFFSET,
                    direction: this.pit.direction ?? 'auto' };
    } else if (sel?.type === 'wall') {
      const w = this.getObj('wall', sel.id);
      selection = w && { type: 'wall', id: sel.id, thickness: w.thickness, restitution: w.restitution, friction: w.friction, material: w.material };
    } else if (sel?.type === 'circle') {
      const c = this.getObj('circle', sel.id);
      selection = c && { type: 'circle', id: sel.id, radius: Math.round(c.radius), restitution: c.restitution, friction: c.friction, material: c.material };
    } else if (sel?.type === 'rect') {
      const r = this.getObj('rect', sel.id);
      selection = r && { type: 'rect', id: sel.id, width: Math.round(r.width), height: Math.round(r.height),
                          angleDeg: Math.round((r.angle ?? 0) * 180 / Math.PI), restitution: r.restitution, friction: r.friction,
                          material: r.material, variant: r.variant ?? 0 };
    } else if (sel?.type === 'poly') {
      const p = this.getObj('poly', sel.id);
      selection = p && { type: 'poly', id: sel.id, thickness: p.thickness, nodes: p.points.length,
                         curved: p.points.filter(n => n.hIn || n.hOut).length, node: this.selNode,
                         ...this.nodeFlags(p),
                         restitution: p.restitution, friction: p.friction, material: p.material };
    } else if (sel?.type === 'road') {
      const a = this.getObj('road', sel.id);
      selection = a && { type: 'road', id: sel.id, nodes: a.points.length, pit: a.pit === true, surface: surfaceOf(a),
                         curved: a.points.filter(n => n.hIn || n.hOut).length, node: this.selNode,
                         ...this.nodeFlags(a) };
    } else if (sel?.type === 'terrain') {
      const a = this.getObj('terrain', sel.id);
      selection = a && { type: 'terrain', id: sel.id, terrain: terrainOf(a), nodes: a.points.length,
                         curved: a.points.filter(n => n.hIn || n.hOut).length, node: this.selNode,
                         ...this.nodeFlags(a) };
    }

    return {
      mode: this.mode,
      hudStatus: this.hudOverride ?? STATUS_MESSAGES[this.mode] ?? '',
      selection,
      objectTree: this.getObjectTree(),
      trackId: this.trackId,
      trackName: this.trackName,
      manualWidth: this.manualWidth,
      manualHeight: this.manualHeight,
      defWidth: this.defWidth,
      defLane: this.defLane,
      defObsThickness: this.defObsThickness,
      defObsRestitution: this.defObsRestitution,
      defObsFriction: this.defObsFriction,
      defObsMaterial: this.defObsMaterial,
      defPolyThickness: this.defPolyThickness,
      defTerrain: this.defTerrain,
      magneticSnap: this.magneticSnap,
      hideObjects: this.hideObjects,
      canCopy: !!sel && ['seg', 'road', 'terrain', 'cp', 'wall', 'circle', 'rect', 'poly'].includes(sel.type),
      hasClipboard: !!this.clipboard,
      canUndo: this.undoStack.length > 0,
      canRedo: this.redoStack.length > 0,
      undoCount: this.undoStack.length,
      redoCount: this.redoStack.length,
      historyLimit: this.historyLimit,
      historyRevision: this.historyRevision,
      areaCount: this.roadAreas.length,
      terrainCount: this.terrainAreas.length,
    };
  }

  notify() {
    this.recordHistory();
    this.invalidateRoadSamples();
    this.onChange(this.getSnapshot());
  }

  captureHistory() {
    return {
      document: structuredClone(Object.fromEntries(HISTORY_FIELDS.map(key => [key, this[key]]))),
      selected: this.selected ? { ...this.selected } : null,
      selNode: this.selNode,
    };
  }

  recordHistory() {
    // A drag or a focused property field is one edit, regardless of intermediate updates.
    if (this.drag || this._historyDepth) return;
    const document = Object.fromEntries(HISTORY_FIELDS.map(key => [key, this[key]]));
    const signature = JSON.stringify(document);
    if (signature !== this._historySignature) {
      this.undoStack.push(this._historyCurrent);
      if (this.undoStack.length > this.historyLimit) this.undoStack.shift();
      this.redoStack = [];
      this._historyCurrent = this.captureHistory();
      this._historySignature = signature;
    } else {
      // Keep the selection to restore with this state without recording navigation as an edit.
      this._historyCurrent.selected = this.selected ? { ...this.selected } : null;
      this._historyCurrent.selNode = this.selNode;
    }
  }

  beginHistoryEdit() {
    this.recordHistory();
    this._historyDepth++;
  }

  endHistoryEdit() {
    if (!this._historyDepth) return;
    this._historyDepth--;
    if (!this._historyDepth) this.notify();
  }

  setHistoryLimit(value) {
    const limit = Number(value);
    if (!Number.isFinite(limit)) return;
    this.historyLimit = Math.max(1, Math.min(1000, Math.trunc(limit)));
    this.undoStack.splice(0, Math.max(0, this.undoStack.length - this.historyLimit));
    this.redoStack.splice(0, Math.max(0, this.redoStack.length - this.historyLimit));
    this.notify();
  }

  restoreHistory(entry) {
    Object.assign(this, structuredClone(entry.document));
    this.selected = entry.selected && this.getObj(entry.selected.type, entry.selected.id)
      ? { ...entry.selected } : null;
    this.selNode = this.selected ? entry.selNode : null;
    this.mode = 'select';
    this.drag = null;
    this.panState = null;
    this.hovered = null;
    this.addFirst = null;
    this.draft = null;
    this.extend = null;
    this.hudOverride = null;
    this.snapTarget = null;
    this.guides = { x: null, y: null };
    this._historyDepth = 0;
    this._boundsCache = null;
    this._historyCurrent = this.captureHistory();
    this._historySignature = JSON.stringify(this._historyCurrent.document);
    this.historyRevision++;
    this.onCursor('default');
    this.notify();
    this.render();
  }

  undo() {
    // Finish any pending edit before navigating history (including a shortcut mid-drag).
    this.drag = null;
    this._historyDepth = 0;
    this.recordHistory();
    if (!this.undoStack.length) return false;
    this.redoStack.push(this.captureHistory());
    this.restoreHistory(this.undoStack.pop());
    return true;
  }

  redo() {
    this.drag = null;
    this._historyDepth = 0;
    this.recordHistory();
    if (!this.redoStack.length) return false;
    this.undoStack.push(this.captureHistory());
    this.restoreHistory(this.redoStack.pop());
    return true;
  }

  /** Anything that moves the road leaves the magnet's cached samples stale (see roadSamples). */
  invalidateRoadSamples() { this._roadSamples = null; }

  // ── Coordinate transforms ───────────────────────────────────
  w2s(x, y) { return { x: (x - this.panX) * this.zoom, y: (y - this.panY) * this.zoom }; }
  s2w(x, y) { return { x: x / this.zoom + this.panX, y: y / this.zoom + this.panY }; }

  mousePos(e) {
    const r = this.canvas.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  }

  // ── Hit testing ──────────────────────────────────────────────
  lineDist(px, py, ax, ay, bx, by) {
    const dx = bx - ax, dy = by - ay;
    const l2 = dx * dx + dy * dy;
    if (l2 === 0) return Math.hypot(px - ax, py - ay);
    const t = Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / l2));
    return Math.hypot(px - (ax + t * dx), py - (ay + t * dy));
  }

  nearHandle(wx, wy, hx, hy) {
    const a = this.w2s(hx, hy);
    const b = this.w2s(wx, wy);
    return Math.hypot(a.x - b.x, a.y - b.y) <= HR + 3;
  }

  bezierSample(x1, y1, cx, cy, x2, y2, steps = 20) {
    const pts = [];
    for (let i = 0; i <= steps; i++) {
      const t = i / steps, mt = 1 - t;
      pts.push({ x: mt * mt * x1 + 2 * mt * t * cx + t * t * x2, y: mt * mt * y1 + 2 * mt * t * cy + t * t * y2 });
    }
    return pts;
  }

  segBezierDist(wx, wy, s) {
    const pts = this.bezierSample(s.x1, s.y1, s.cx, s.cy, s.x2, s.y2, 10);
    let minD = Infinity;
    for (let i = 0; i < pts.length - 1; i++)
      minD = Math.min(minD, this.lineDist(wx, wy, pts[i].x, pts[i].y, pts[i + 1].x, pts[i + 1].y));
    return minD;
  }

  // ── Bezier node lists (road areas, polygon obstacles) ───────
  // A node list is a cubic Bezier chain: a node may carry `hOut` (the handle leaving it toward the
  // next node) and `hIn` (the handle the previous edge arrives at). A missing handle falls back to
  // the edge's third point, which is exactly the straight line — so a handle is ALWAYS there to be
  // grabbed (drawn faint until it is real), and an edge becomes a curve just by dragging one.
  edgeControls(nodes, i, closed) {
    const a = nodes[i];
    const b = closed ? nodes[(i + 1) % nodes.length] : nodes[i + 1];
    if (!a || !b) return null;
    const dx = b.x - a.x, dy = b.y - a.y;
    return {
      a, b,
      c1: a.hOut ?? { x: a.x + dx / 3, y: a.y + dy / 3 },
      c2: b.hIn ?? { x: a.x + (dx * 2) / 3, y: a.y + (dy * 2) / 3 },
      straight: !a.hOut && !b.hIn,
    };
  }

  /** The two handles around node `i` (null at an open end, where there is no edge on that side). */
  nodeHandles(nodes, i, closed) {
    const next = this.edgeControls(nodes, i, closed);
    const last = nodes.length - 1;
    const prevIndex = i > 0 ? i - 1 : closed ? last : null;
    const prev = prevIndex != null ? this.edgeControls(nodes, prevIndex, closed) : null;
    return { out: next ? next.c1 : null, in: prev ? prev.c2 : null };
  }

  /** One edge sampled into points. A straight edge stays two points: there is nothing in between. */
  sampleEdge(e) {
    if (e.straight) return [{ x: e.a.x, y: e.a.y }, { x: e.b.x, y: e.b.y }];
    const hull = Math.hypot(e.c1.x - e.a.x, e.c1.y - e.a.y)
      + Math.hypot(e.c2.x - e.c1.x, e.c2.y - e.c1.y)
      + Math.hypot(e.b.x - e.c2.x, e.b.y - e.c2.y);
    const steps = Math.max(4, Math.min(EDGE_STEP_MAX, Math.ceil(hull / EDGE_STEP)));
    const pts = [];
    for (let s = 0; s <= steps; s++) {
      const t = s / steps, mt = 1 - t;
      pts.push({
        x: mt * mt * mt * e.a.x + 3 * mt * mt * t * e.c1.x + 3 * mt * t * t * e.c2.x + t * t * t * e.b.x,
        y: mt * mt * mt * e.a.y + 3 * mt * mt * t * e.c1.y + 3 * mt * t * t * e.c2.y + t * t * t * e.b.y,
      });
    }
    return pts;
  }

  /**
   * The whole node list as a polyline (a closed list has one extra edge back to node 0). This is the
   * shape the editor draws, hit-tests AND exports: the game reads polygons and polylines, so the
   * curves are flattened here once and every consumer sees the same geometry.
   */
  shapePoints(obj, closed = !!obj?.closed) {
    const nodes = obj?.points ?? [];
    if (nodes.length < 2) return [];
    const count = closed ? nodes.length : nodes.length - 1;
    const out = [];
    for (let i = 0; i < count; i++) {
      const pts = this.sampleEdge(this.edgeControls(nodes, i, closed));
      out.push(...(out.length ? pts.slice(1) : pts));
    }
    return out;
  }

  /** Nearest point of edge `i`: its distance from (wx, wy) and the parameter `t` along the edge. */
  closestOnEdge(nodes, i, closed, wx, wy) {
    const e = this.edgeControls(nodes, i, closed);
    if (!e) return null;
    const STEPS = 24;
    let best = null;
    let prev = { x: e.a.x, y: e.a.y };
    for (let s = 1; s <= STEPS; s++) {
      const t = s / STEPS, mt = 1 - t;
      const point = {
        x: mt * mt * mt * e.a.x + 3 * mt * mt * t * e.c1.x + 3 * mt * t * t * e.c2.x + t * t * t * e.b.x,
        y: mt * mt * mt * e.a.y + 3 * mt * mt * t * e.c1.y + 3 * mt * t * t * e.c2.y + t * t * t * e.b.y,
      };
      const dx = point.x - prev.x, dy = point.y - prev.y;
      const len2 = dx * dx + dy * dy;
      const u = len2 > 0 ? Math.max(0, Math.min(1, ((wx - prev.x) * dx + (wy - prev.y) * dy) / len2)) : 0;
      const dist = Math.hypot(wx - (prev.x + dx * u), wy - (prev.y + dy * u));
      if (!best || dist < best.dist) best = { dist, t: (s - 1 + u) / STEPS };
      prev = point;
    }
    return best;
  }

  closestEdge(nodes, closed, wx, wy) {
    const count = closed ? nodes.length : nodes.length - 1;
    let best = null;
    for (let i = 0; i < count; i++) {
      const hit = this.closestOnEdge(nodes, i, closed, wx, wy);
      if (hit && (!best || hit.dist < best.dist)) best = { edge: i, ...hit };
    }
    return best;
  }

  /**
   * Adds a node at parameter `t` of edge `i`. The curve keeps its exact shape (De Casteljau split),
   * which is what makes it safe to refine a curve node by node instead of re-drawing it: split as
   * many times as you like and the outline never moves. A straight edge stays straight (no handles
   * are invented), so a plain polygon stays a plain polygon.
   */
  insertNode(nodes, i, closed, t) {
    const e = this.edgeControls(nodes, i, closed);
    if (!e) return -1;
    const lerp = (a, b) => ({ x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t });
    const a1 = lerp(e.a, e.c1), m1 = lerp(e.c1, e.c2), b1 = lerp(e.c2, e.b);
    const a2 = lerp(a1, m1), b2 = lerp(m1, b1);
    const at = lerp(a2, b2);
    const node = { x: at.x, y: at.y };
    if (!e.straight) {
      e.a.hOut = { x: a1.x, y: a1.y };
      node.hIn = { x: a2.x, y: a2.y };
      node.hOut = { x: b2.x, y: b2.y };
      e.b.hIn = { x: b1.x, y: b1.y };
    }
    const index = closed && i === nodes.length - 1 ? nodes.length : i + 1;
    nodes.splice(index, 0, node);
    return index;
  }

  /** A copy of `node` translated by (dx, dy), handles included. */
  shiftNode(node, dx, dy) {
    const out = { x: node.x + dx, y: node.y + dy };
    if (node.hIn) out.hIn = { x: node.hIn.x + dx, y: node.hIn.y + dy };
    if (node.hOut) out.hOut = { x: node.hOut.x + dx, y: node.hOut.y + dy };
    return out;
  }

  /** Moves node `i` to (x, y); its handles come along, so the curve keeps its shape. */
  moveNode(obj, i, x, y) {
    const node = obj.points?.[i];
    if (!node) return;
    const dx = x - node.x, dy = y - node.y;
    node.x = x; node.y = y;
    if (node.hIn) { node.hIn.x += dx; node.hIn.y += dy; }
    if (node.hOut) { node.hOut.x += dx; node.hOut.y += dy; }
  }

  /**
   * Moves one Bezier handle of node `i` to (x, y). With `mirror` the opposite handle follows on the
   * same line (a smooth node, the default): that is what keeps a curve flowing through its node
   * instead of making a corner out of it, and it is skipped where that side has no edge.
   */
  moveHandle(obj, i, side, x, y, mirror) {
    const nodes = obj.points ?? [];
    const node = nodes[i];
    if (!node) return;
    const field = side === 'out' ? 'hOut' : 'hIn';
    node[field] = { x, y };
    if (!mirror) return;
    const hasSide = side === 'out' ? (!!obj.closed || i < nodes.length - 1) : (!!obj.closed || i > 0);
    const dx = x - node.x, dy = y - node.y;
    if (!hasSide || Math.hypot(dx, dy) < HANDLE_MIN) return;
    node[side === 'out' ? 'hIn' : 'hOut'] = { x: node.x - dx, y: node.y - dy };
  }

  // ── Alignment aids ──────────────────────────────────────────
  /**
   * Every point worth lining up with: the ends and middles of the other elements, plus their curve
   * controls and the corners of a segment's RIBBON — its border, which is what a road area has to sit
   * flush against. The centre line's ends alone are not enough: they line a node up with the middle of
   * the road while its edge is half a width away.
   */
  alignTargets(exclude = null) {
    const out = [];
    const skipped = (type, id) => exclude && exclude.type === type && exclude.id === id;
    const pushEnds = (o) => { out.push({ x: o.x1, y: o.y1 }, { x: o.x2, y: o.y2 }); };
    for (const s of this.segs) {
      if (skipped('seg', s.id)) continue;
      pushEnds(s);
      out.push({ x: (s.x1 + s.x2) / 2, y: (s.y1 + s.y2) / 2 });
      if (s.cx != null) out.push({ x: s.cx, y: s.cy });
      out.push(...this.segCorners(s));
    }
    for (const area of this.roadAreas) {
      if (skipped('road', area.id)) continue;
      for (const p of area.points ?? []) out.push({ x: p.x, y: p.y });
    }
    for (const o of this.obstacles) {
      if (skipped(o.type, o.id)) continue;
      out.push(...this.magnetPoints(o));
      // A wall's FACES are what it has to meet other things with, and they are not its centre line:
      // offering the outline's corners is what makes "flush against this stand" line up (same reason a
      // segment offers its ribbon corners, see segCorners).
      out.push(...this.obstacleOutlineCorners(o));
    }
    for (const c of this.cps) {
      if (skipped('cp', c.id)) continue;
      pushEnds(c);
    }
    if (this.sg && !skipped('sg', this.sg.id)) pushEnds(this.sg);
    if (this.pit && !skipped('pit', this.pit.id)) pushEnds(this.pit);
    return out;
  }

  /**
   * The four corners of a segment's drawn ribbon (its border, where the road actually ends): the ends
   * of the ribbon, each taken half a width to either side of the centre line.
   */
  segCorners(s) {
    const points = s.cx != null
      ? this.bezierSample(s.x1, s.y1, s.cx, s.cy, s.x2, s.y2, 8)
      : [{ x: s.x1, y: s.y1 }, { x: s.x2, y: s.y2 }];
    const half = (s.width ?? 180) / 2;
    const out = [];
    for (const index of [0, points.length - 1]) {
      const previous = points[Math.max(0, index - 1)];
      const next = points[Math.min(points.length - 1, index + 1)];
      const dx = next.x - previous.x, dy = next.y - previous.y;
      const length = Math.hypot(dx, dy);
      if (length < 0.01) continue;
      const nx = (-dy / length) * half, ny = (dx / length) * half;
      out.push({ x: points[index].x + nx, y: points[index].y + ny });
      out.push({ x: points[index].x - nx, y: points[index].y - ny });
    }
    return out;
  }

  /**
   * The points of an obstacle the magnet works with: what something else lines up with when IT is
   * dragged, and what this obstacle offers when it is the one being dragged (the whole shape follows
   * whichever of its points catches, see magnetOffset).
   *
   * A rectangle offers its middle and its four corners — rotated with it, the shape you actually see and
   * the thing you want flush against the next rectangle. A circle offers its middle and the four points
   * of its box, because a stack of tyres is lined up by its edge as often as by its centre. A polygon
   * offers its nodes, a wall its two ends.
   */
  magnetPoints(o) {
    if (o.type === 'rect') {
      const hw = o.width / 2, hh = o.height / 2;
      const cos = Math.cos(o.angle ?? 0), sin = Math.sin(o.angle ?? 0);
      return [{ x: o.x, y: o.y }, ...[[-hw, -hh], [hw, -hh], [hw, hh], [-hw, hh]]
        .map(([x, y]) => ({ x: o.x + x * cos - y * sin, y: o.y + x * sin + y * cos }))];
    }
    if (o.type === 'circle') {
      return [
        { x: o.x, y: o.y },
        { x: o.x - o.radius, y: o.y }, { x: o.x + o.radius, y: o.y },
        { x: o.x, y: o.y - o.radius }, { x: o.x, y: o.y + o.radius },
      ];
    }
    if (o.type === 'poly') return (o.points ?? []).map((p) => ({ x: p.x, y: p.y }));
    return [{ x: o.x1, y: o.y1 }, { x: o.x2, y: o.y2 }];
  }

  /** The four corners of a wall's drawn body (its centre line moved half a thickness to each side). */
  obstacleOutlineCorners(o) {
    if (o.type !== 'wall') return [];
    const thickness = o.thickness ?? 20;
    return this.segCorners({ x1: o.x1, y1: o.y1, x2: o.x2, y2: o.y2, width: thickness });
  }

  /**
   * The magnet, in one place: the nearest node within SNAP_R, or else the road itself (its edge, or
   * its centre line) within ROAD_SNAP_R, or else the nearest alignment line (same X, or same Y, as
   * another element's node) within GUIDE_R. Returns both the point to use and the guides to draw, so
   * what the canvas shows is exactly what the click will do.
   *
   * The road is in here because a wall is placed BY HAND — it does not follow the asphalt on its own
   * — and an element's nodes are far apart along a bend: without the road's own curve to catch, a
   * barrier ends up a hand's width away from the edge it is supposed to run along.
   */
  magnetFor(x, y, exclude = null) {
    if (!this.magneticSnap) return { x, y, guides: { x: null, y: null } };
    const point = this.findSnapPoint(x, y, exclude);
    if (point) return { x: point.x, y: point.y, guides: { x: point.x, y: point.y } };

    const onRoad = this.nearestOnRoad(x, y, ROAD_SNAP_R / this.zoom);
    if (onRoad) return { x: onRoad.x, y: onRoad.y, guides: { x: onRoad.x, y: onRoad.y } };

    const tol = GUIDE_R / this.zoom;
    let gx = null, gy = null, bestX = tol, bestY = tol;
    for (const target of this.alignTargets(exclude)) {
      const dx = Math.abs(target.x - x);
      if (dx < bestX) { bestX = dx; gx = target.x; }
      const dy = Math.abs(target.y - y);
      if (dy < bestY) { bestY = dy; gy = target.y; }
    }
    return { x: gx ?? x, y: gy ?? y, guides: { x: gx, y: gy } };
  }

  /**
   * The road's pieces as the magnet needs them: sampled, so the search below walks straight segments
   * instead of re-sampling Bezier curves on every mouse move. Kept until the track changes. Each
   * sample keeps the piece it came from, so callers can tell the pit lane from the road itself.
   */
  roadSamples() {
    if (this._roadSamples) return this._roadSamples;
    const main = this.segs.filter((s) => (s.lane ?? 'main') === 'main');
    const roadWidth = this.modalWidth(main.length ? main : this.segs);
    this._roadSamples = buildPieces({ segs: this.segs, areas: this.roadAreas, roadWidth })
      .map((piece) => ({ ...samplePiece(piece, { roadWidth }), piece }))
      .filter((sampled) => sampled.points.length >= 2);
    return this._roadSamples;
  }

  /**
   * The nearest point of the ROAD to (wx, wy) — the edge, or the centre line — within `reach`, else
   * `null` so the magnet stays out of the way.
   *
   * A band's edge is its centre line moved half a width sideways, on the side the point is on; the
   * centre line is offered as well, and the nearer of the two wins: clicking on the asphalt takes the
   * centre, clicking beside it takes the edge, which is the whole of what "put it on the track" means
   * when the element is placed by hand. An AREA has no centre line — its outline IS its edge.
   */
  nearestOnRoad(wx, wy, reach) {
    let best = null;
    const keep = (x, y, d) => { if (!best || d < best.d) best = { x, y, d }; };
    for (const sampled of this.roadSamples()) {
      const points = sampled.points;
      const last = sampled.closed ? points.length : points.length - 1;
      for (let i = 0; i < last; i++) {
        const a = points[i], b = points[(i + 1) % points.length];
        const dx = b.x - a.x, dy = b.y - a.y;
        const lengthSq = dx * dx + dy * dy;
        if (lengthSq < 0.0001) continue;
        const t = Math.max(0, Math.min(1, ((wx - a.x) * dx + (wy - a.y) * dy) / lengthSq));
        const px = a.x + dx * t, py = a.y + dy * t;
        keep(px, py, Math.hypot(wx - px, wy - py));
        if (!sampled.band) continue;
        const length = Math.sqrt(lengthSq);
        const half = ((a.w ?? 180) + (b.w ?? 180)) / 4;
        const nx = -dy / length, ny = dx / length;
        const side = (wx - px) * nx + (wy - py) * ny >= 0 ? 1 : -1;
        const ex = px + nx * half * side, ey = py + ny * half * side;
        keep(ex, ey, Math.hypot(wx - ex, wy - ey));
      }
    }
    return best && best.d <= reach ? best : null;
  }

  /**
   * The magnet applied to an object that is being moved AS A WHOLE: every one of its own corners (a
   * segment's two ends, a shape's nodes) is offered to the magnet and the whole object follows the one
   * that snaps, by the offset the magnet asks for. Snapping the MOUSE instead — which is what dragging
   * a segment's body used to do, by hand — means grabbing a segment by its middle snaps its middle,
   * which is not a point of the road at all, and a shape moved by its body snapped nowhere.
   */
  magnetOffset(points, dx, dy, exclude = null) {
    if (!this.magneticSnap) return { offX: dx, offY: dy, guides: { x: null, y: null }, snapped: null };
    let best = null;
    for (const point of points) {
      const at = this.magnetFor(point.x + dx, point.y + dy, exclude);
      const offX = at.x - point.x;
      const offY = at.y - point.y;
      const shift = Math.hypot(offX - dx, offY - dy);
      if (shift < 0.01) continue; // this corner didn't snap: nothing to follow
      // A corner landing ON another node beats one that only lines up with it: putting two nodes
      // together is the intent, an alignment line is a hint.
      const score = at.guides.x != null && at.guides.y != null ? 0 : 1;
      if (!best || score < best.score || (score === best.score && shift < best.shift)) {
        best = { score, shift, offX, offY, guides: at.guides, snapped: { x: point.x + offX, y: point.y + offY } };
      }
    }
    return best ?? { offX: dx, offY: dy, guides: { x: null, y: null }, snapped: null };
  }

  /** Dashed lines through the point being placed/dragged, where it lines up with another element. */
  drawGuides() {
    const { x, y } = this.guides;
    if (x == null && y == null) return;
    const ctx = this.ctx;
    ctx.save();
    ctx.strokeStyle = '#22d3ee';
    ctx.lineWidth = 1;
    ctx.setLineDash([6, 4]);
    if (x != null) {
      const sx = this.w2s(x, 0).x;
      ctx.beginPath(); ctx.moveTo(sx, 0); ctx.lineTo(sx, this.canvas.height); ctx.stroke();
    }
    if (y != null) {
      const sy = this.w2s(0, y).y;
      ctx.beginPath(); ctx.moveTo(0, sy); ctx.lineTo(this.canvas.width, sy); ctx.stroke();
    }
    ctx.restore();
  }

  hitTest(wx, wy) {
    if (this.selected) {
      const o = this.getObj(this.selected.type, this.selected.id);
      if (o) {
        if (this.selected.type === 'circle') {
          if (this.nearHandle(wx, wy, o.x, o.y)) return { ...this.selected, handle: 'center' };
          if (this.nearHandle(wx, wy, o.x + o.radius, o.y)) return { ...this.selected, handle: 'radius' };
        } else if (this.selected.type === 'rect') {
          if (this.nearHandle(wx, wy, o.x, o.y)) return { ...this.selected, handle: 'center' };
          const spin = this.rectSpinHandle(o);
          if (this.nearHandle(wx, wy, spin.x, spin.y)) return { ...this.selected, handle: 'rotate' };
          const corner = this.rectCorner(o);
          if (this.nearHandle(wx, wy, corner.x, corner.y)) return { ...this.selected, handle: 'resize' };
        } else if (this.selected.type === 'poly' || this.selected.type === 'road' || this.selected.type === 'terrain') {
          // Node handles first: they sit right next to their node, so they have to win the hit test.
          const nodes = o.points ?? [];
          if (this.selNode != null && nodes[this.selNode]) {
            const h = this.nodeHandles(nodes, this.selNode, !!o.closed);
            if (h.out && this.nearHandle(wx, wy, h.out.x, h.out.y)) return { ...this.selected, handle: `h${this.selNode}o` };
            if (h.in && this.nearHandle(wx, wy, h.in.x, h.in.y)) return { ...this.selected, handle: `h${this.selNode}i` };
          }
          for (let i = 0; i < nodes.length; i++) {
            if (this.nearHandle(wx, wy, nodes[i].x, nodes[i].y)) return { ...this.selected, handle: `n${i}` };
          }
          const c = this.polylineCenter(o);
          if (this.nearHandle(wx, wy, c.x, c.y)) return { ...this.selected, handle: 'mid' };
        } else {
          if (this.nearHandle(wx, wy, o.x1, o.y1)) return { ...this.selected, handle: 'p1' };
          if (this.nearHandle(wx, wy, o.x2, o.y2)) return { ...this.selected, handle: 'p2' };
          if (this.selected.type === 'seg') {
            if (o.cx != null && this.nearHandle(wx, wy, o.cx, o.cy)) return { ...this.selected, handle: 'ctrl' };
          } else {
            const mx = (o.x1 + o.x2) / 2, my = (o.y1 + o.y2) / 2;
            if (this.nearHandle(wx, wy, mx, my)) return { ...this.selected, handle: 'mid' };
          }
        }
      }
    }

    // Body priority for both hover and click: race logic, road, obstacles, terrain.
    const cpThresh = CP_CLICK / this.zoom;
    for (let i = this.cps.length - 1; i >= 0; i--) {
      const c = this.cps[i];
      if (this.lineDist(wx, wy, c.x1, c.y1, c.x2, c.y2) <= cpThresh)
        return { type: 'cp', id: c.id, handle: 'body' };
    }

    if (this.sg && this.lineDist(wx, wy, this.sg.x1, this.sg.y1, this.sg.x2, this.sg.y2) <= cpThresh)
      return { type: 'sg', id: 'sg', handle: 'body' };

    if (this.pit) {
      const px1 = Math.min(this.pit.x1, this.pit.x2), px2 = Math.max(this.pit.x1, this.pit.x2);
      const py1 = Math.min(this.pit.y1, this.pit.y2), py2 = Math.max(this.pit.y1, this.pit.y2);
      if (this.lineDist(wx, wy, px1, py1, px2, py1) <= cpThresh ||
          this.lineDist(wx, wy, px2, py1, px2, py2) <= cpThresh ||
          this.lineDist(wx, wy, px2, py2, px1, py2) <= cpThresh ||
          this.lineDist(wx, wy, px1, py2, px1, py1) <= cpThresh ||
          (wx >= px1 && wx <= px2 && wy >= py1 && wy <= py2))
        return { type: 'pit', id: 'pit', handle: 'body' };
    }

    for (let i = this.segs.length - 1; i >= 0; i--) {
      const s = this.segs[i];
      const d = s.cx != null ? this.segBezierDist(wx, wy, s) : this.lineDist(wx, wy, s.x1, s.y1, s.x2, s.y2);
      if (d <= s.width / 2 + 5 / this.zoom)
        return { type: 'seg', id: s.id, handle: 'body' };
    }

    // Road areas sit UNDER the segments on screen (they are the surface the pieces are drawn on), so
    // a click inside both picks the segment.
    for (let i = this.roadAreas.length - 1; i >= 0; i--) {
      const area = this.roadAreas[i];
      if (this.areaHit(wx, wy, area)) return { type: 'road', id: area.id, handle: 'body' };
    }

    for (let i = this.obstacles.length - 1; i >= 0; i--) {
      const o = this.obstacles[i];
      if (this.obsHit(wx, wy, o)) return { type: o.type, id: o.id, handle: 'body' };
    }

    // Terrain areas are the ground under everything: picked last of the shapes, so a road or an
    // obstacle drawn over one still wins the click.
    for (let i = this.terrainAreas.length - 1; i >= 0; i--) {
      const area = this.terrainAreas[i];
      if (this.areaHit(wx, wy, area)) return { type: 'terrain', id: area.id, handle: 'body' };
    }

    return null;
  }

  // Hit test for a road area: anywhere inside it, or on its outline (so a thin shape is still
  // grabbable).
  areaHit(wx, wy, area) {
    const points = this.shapePoints(area, true);
    if (points.length < 3) return false;
    if (this.polylineDist(wx, wy, [...points, points[0]]) <= 5 / this.zoom) return true;
    let inside = false;
    for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
      const a = points[i], b = points[j];
      if ((a.y > wy) !== (b.y > wy) && wx < ((b.x - a.x) * (wy - a.y)) / (b.y - a.y) + a.x) inside = !inside;
    }
    return inside;
  }

  // World-space position of the resize handle for a rect obstacle (rotated bottom-right corner).
  rectCorner(o) {
    const hw = o.width / 2, hh = o.height / 2;
    const cos = Math.cos(o.angle ?? 0), sin = Math.sin(o.angle ?? 0);
    return { x: o.x + hw * cos - hh * sin, y: o.y + hw * sin + hh * cos };
  }

  // The rotate handle hangs off the middle of the rect's top edge. Its gap is a screen distance, not
  // a world one, so it stays put under the cursor at any zoom instead of drifting onto the corner
  // handle (which would make the two impossible to tell apart).
  rectSpinHandle(o) {
    const hh = o.height / 2;
    const cos = Math.cos(o.angle ?? 0), sin = Math.sin(o.angle ?? 0);
    const ly = -hh - (HR + 18) / this.zoom;
    return { x: o.x - ly * sin, y: o.y + ly * cos };
  }

  // Hit-test for obstacle bodies (wall/circle/rect), each with its own geometry.
  // Minimum distance from (wx, wy) to any edge of a polyline, or Infinity if it has fewer than
  // 2 nodes. Polygon obstacles are walls of `thickness` centred on each of their edges.
  polylineDist(wx, wy, points) {
    let minD = Infinity;
    for (let i = 0; i < points.length - 1; i++) {
      minD = Math.min(minD, this.lineDist(wx, wy, points[i].x, points[i].y, points[i + 1].x, points[i + 1].y));
    }
    return minD;
  }

  // Middle (average) of a polygon's nodes — used as its whole-shape move handle.
  polylineCenter(o) {
    const pts = o.points ?? [];
    if (!pts.length) return { x: 0, y: 0 };
    let sx = 0, sy = 0;
    for (const p of pts) { sx += p.x; sy += p.y; }
    return { x: sx / pts.length, y: sy / pts.length };
  }

  obsHit(wx, wy, o) {
    const pad = 5 / this.zoom;
    if (o.type === 'circle') return Math.hypot(wx - o.x, wy - o.y) <= o.radius + pad;
    if (o.type === 'rect') {
      const angle = o.angle ?? 0;
      const cos = Math.cos(-angle), sin = Math.sin(-angle);
      const dx = wx - o.x, dy = wy - o.y;
      const lx = dx * cos - dy * sin, ly = dx * sin + dy * cos;
      return Math.abs(lx) <= o.width / 2 + pad && Math.abs(ly) <= o.height / 2 + pad;
    }
    if (o.type === 'poly') return this.polylineDist(wx, wy, this.shapePoints(o, false)) <= (o.thickness ?? this.defPolyThickness) / 2 + pad;
    return this.lineDist(wx, wy, o.x1, o.y1, o.x2, o.y2) <= o.thickness / 2 + pad;
  }

  // Nearest magnetic snap candidate (segment endpoints + polygon nodes) within SNAP_R screen px,
  // or null. `exclude` is the `{ type, id }` of the object being drawn/dragged, so it never snaps
  // to itself.
  findSnapPoint(wx, wy, exclude = null) {
    if (!this.magneticSnap) return null;
    const thresh = SNAP_R / this.zoom;
    let best = null, bestDist = thresh;
    const consider = (x, y) => {
      const d = Math.hypot(wx - x, wy - y);
      if (d < bestDist) { bestDist = d; best = { x, y }; }
    };
    for (const s of this.segs) {
      if (exclude?.type === 'seg' && exclude.id === s.id) continue;
      consider(s.x1, s.y1);
      consider(s.x2, s.y2);
      for (const corner of this.segCorners(s)) consider(corner.x, corner.y);
    }
    for (const area of this.roadAreas) {
      if (exclude?.type === 'road' && exclude.id === area.id) continue;
      for (const p of area.points ?? []) consider(p.x, p.y);
    }
    for (const area of this.terrainAreas) {
      if (exclude?.type === 'terrain' && exclude.id === area.id) continue;
      for (const p of area.points ?? []) consider(p.x, p.y);
    }
    for (const o of this.obstacles) {
      if (exclude?.type === o.type && exclude.id === o.id) continue;
      for (const p of this.magnetPoints(o)) consider(p.x, p.y);
      for (const corner of this.obstacleOutlineCorners(o)) consider(corner.x, corner.y);
    }
    return best;
  }

  // ── Rendering ────────────────────────────────────────────────
  render() {
    if (!this.ctx) return;
    const ctx = this.ctx, W = this.canvas.width, H = this.canvas.height;
    ctx.clearRect(0, 0, W, H);
    ctx.fillStyle = '#0d1117';
    ctx.fillRect(0, 0, W, H);

    this.drawGrid();
    this.drawBounds();

    this.ctx.save();
    if (this.hideObjects) this.ctx.globalAlpha = 0.1;
    // Terrain areas are the ground, so they are drawn first — the road, the objects and everything
    // else sit on top of them (and in game the asphalt covers them too).
    for (const area of this.terrainAreas) {
      const sel = this.selected?.type === 'terrain' && this.selected.id === area.id;
      const hov = !sel && this.hovered?.type === 'terrain' && this.hovered.id === area.id;
      this.drawTerrain(area, sel, hov);
    }
    for (const area of this.roadAreas) {
      const sel = this.selected?.type === 'road' && this.selected.id === area.id;
      const hov = !sel && this.hovered?.type === 'road' && this.hovered.id === area.id;
      this.drawArea(area, sel, hov);
    }
    for (const s of this.segs) {
      const sel = this.selected?.type === 'seg' && this.selected.id === s.id;
      const hov = !sel && this.hovered?.type === 'seg' && this.hovered.id === s.id;
      this.drawSeg(s, sel, hov);
    }
    for (const c of this.cps) {
      const sel = this.selected?.type === 'cp' && this.selected.id === c.id;
      const hov = !sel && this.hovered?.type === 'cp' && this.hovered.id === c.id;
      this.drawCp(c, sel, hov);
    }
    if (this.sg) {
      const sel = this.selected?.type === 'sg';
      const hov = !sel && this.hovered?.type === 'sg';
      this.drawSg(this.sg, sel, hov);
    }
    if (this.pit) {
      const sel = this.selected?.type === 'pit';
      const hov = !sel && this.hovered?.type === 'pit';
      this.drawPit(this.pit, sel, hov);
    }
    // All tyre sprites share one depth pass, including piles in different obstacles.
    this._tyreDrawQueue = [];
    for (const o of this.obstacles) {
      const sel = this.selected?.type === o.type && this.selected.id === o.id;
      const hov = !sel && this.hovered?.type === o.type && this.hovered.id === o.id;
      this.drawObstacle(o, sel, hov);
    }
    const tyreDraws = this._tyreDrawQueue;
    this._tyreDrawQueue = null;
    this.drawTyreStacks(tyreDraws);
    // Keep selected outlines/handles above the sprites without drawing the stacks twice.
    const selectedTyre = this.obstacles.find(o => this.isTyreMaterial(o.material)
      && this.selected?.type === o.type && this.selected.id === o.id);
    if (selectedTyre) {
      this._tyreDrawQueue = [];
      this.drawObstacle(selectedTyre, true, false);
      this._tyreDrawQueue = null;
    }
    this.ctx.restore();

    if (this.mode !== 'select') this.drawPreview();
    if (this.snapTarget) this.drawSnapIndicator(this.snapTarget);
    this.drawGuides();
  }

  // World-space bounding points for an obstacle, used by getBoundsRect()/fitView().
  obstaclePoints(o) {
    if (o.type === 'circle') return [{ x: o.x - o.radius, y: o.y - o.radius }, { x: o.x + o.radius, y: o.y + o.radius }];
    if (o.type === 'poly') return (o.points ?? []).map(p => ({ x: p.x, y: p.y }));
    if (o.type === 'rect') {
      const hw = o.width / 2, hh = o.height / 2;
      const cos = Math.cos(o.angle ?? 0), sin = Math.sin(o.angle ?? 0);
      return [[-hw, -hh], [hw, -hh], [hw, hh], [-hw, hh]]
        .map(([x, y]) => ({ x: o.x + x * cos - y * sin, y: o.y + x * sin + y * cos }));
    }
    return [{ x: o.x1, y: o.y1 }, { x: o.x2, y: o.y2 }];
  }

  /**
   * The game area, as the dashed box draws it: a rectangle anchored at the world ORIGIN (the game
   * paints the surface into `width` by `height` from there — see gameAreaPlan), so an automatic one
   * runs to the far corner of the content plus the margin.
   */
  getBoundsRect() {
    const manualW = this.manualWidth || 0;
    const manualH = this.manualHeight || 0;
    const box = this.contentBBox();
    if (!manualW && !manualH && !box) return null;

    const w = manualW || Math.round((box?.maxX ?? 0) + AREA_MARGIN);
    const h = manualH || Math.round((box?.maxY ?? 0) + AREA_MARGIN);
    return { rx: 0, ry: 0, w, h };
  }

  drawBounds() {
    if (!this.drag) this._boundsCache = this.getBoundsRect();
    const b = this._boundsCache;
    if (!b) return;
    const ctx = this.ctx;
    const { rx, ry, w, h } = b;
    const tl = this.w2s(rx, ry);
    const br = this.w2s(rx + w, ry + h);
    const sw = br.x - tl.x, sh = br.y - tl.y;

    ctx.save();
    if (this.bgImage) {
      ctx.globalAlpha = this.hideObjects ? 1 : 0.45;
      ctx.drawImage(this.bgImage, tl.x, tl.y, sw, sh);
      ctx.globalAlpha = 1;
    }
    ctx.strokeStyle = '#eab308';
    ctx.lineWidth = 2;
    ctx.setLineDash([8, 5]);
    ctx.strokeRect(tl.x, tl.y, sw, sh);
    ctx.setLineDash([]);
    ctx.font = '11px system-ui, sans-serif';
    ctx.fillStyle = '#eab308';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'bottom';
    ctx.fillText(`${w} \u00d7 ${h}`, tl.x + 4, tl.y - 3);
    ctx.restore();
  }

  drawGrid() {
    const ctx = this.ctx;
    const tl = this.s2w(0, 0), br = this.s2w(this.canvas.width, this.canvas.height);
    for (const [step, color] of [[500, '#161b22'], [1000, '#1e2a3a']]) {
      ctx.strokeStyle = color;
      ctx.lineWidth = 1;
      for (let x = Math.floor(tl.x / step) * step; x <= br.x + step; x += step) {
        const { x: sx } = this.w2s(x, 0);
        ctx.beginPath(); ctx.moveTo(sx, 0); ctx.lineTo(sx, this.canvas.height); ctx.stroke();
      }
      for (let y = Math.floor(tl.y / step) * step; y <= br.y + step; y += step) {
        const { y: sy } = this.w2s(0, y);
        ctx.beginPath(); ctx.moveTo(0, sy); ctx.lineTo(this.canvas.width, sy); ctx.stroke();
      }
    }
  }

  segRibbon(s) {
    const pts = s.cx != null
      ? this.bezierSample(s.x1, s.y1, s.cx, s.cy, s.x2, s.y2, 20)
      : [{ x: s.x1, y: s.y1 }, { x: s.x2, y: s.y2 }];
    const half = s.width / 2;
    const left = [], right = [], center = pts.map(p => this.w2s(p.x, p.y));
    for (let i = 0; i < pts.length; i++) {
      const prev = pts[Math.max(0, i - 1)], next = pts[Math.min(pts.length - 1, i + 1)];
      const dx = next.x - prev.x, dy = next.y - prev.y;
      const len = Math.hypot(dx, dy);
      if (len < 0.01) { left.push(center[i]); right.push(center[i]); continue; }
      const nx = -dy / len * half, ny = dx / len * half;
      left.push(this.w2s(pts[i].x + nx, pts[i].y + ny));
      right.push(this.w2s(pts[i].x - nx, pts[i].y - ny));
    }
    return { left, right, center };
  }

  drawSeg(s, sel, hov) {
    const ctx = this.ctx;
    const { left, right, center } = this.segRibbon(s);
    if (left.length < 2) return;
    const p1 = center[0], p2 = center[center.length - 1];

    ctx.beginPath();
    ctx.moveTo(left[0].x, left[0].y);
    for (let i = 1; i < left.length; i++) ctx.lineTo(left[i].x, left[i].y);
    for (let i = right.length - 1; i >= 0; i--) ctx.lineTo(right[i].x, right[i].y);
    ctx.closePath();

    const isPit = (s.lane ?? 'main') !== 'main';
    const lc = isPit ? {
      fill: sel ? '#2d1e00' : hov ? '#221600' : '#191000',
      stroke: sel ? '#f59e0b' : hov ? '#b45309' : '#5c3a00',
      center: sel ? '#fbbf24' : '#92400e',
      handle: '#f59e0b', ctrl: '#fb923c'
    } : {
      fill: sel ? '#1e3a5f' : hov ? '#2d3748' : '#1f2937',
      stroke: sel ? '#60a5fa' : hov ? '#9ca3af' : '#4b5563',
      center: sel ? '#93c5fd' : '#6b7280',
      handle: '#60a5fa', ctrl: '#a78bfa'
    };
    ctx.fillStyle = lc.fill; ctx.strokeStyle = lc.stroke; ctx.lineWidth = sel ? 2 : hov ? 1.5 : 1;
    ctx.fill();
    ctx.stroke();

    ctx.setLineDash([Math.max(4, 10 * this.zoom), Math.max(3, 8 * this.zoom)]);
    ctx.strokeStyle = lc.center;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(center[0].x, center[0].y);
    for (let i = 1; i < center.length; i++) ctx.lineTo(center[i].x, center[i].y);
    ctx.stroke();
    ctx.setLineDash([]);

    if (sel) {
      this.drawHandle(p1.x, p1.y, lc.handle);
      this.drawHandle(p2.x, p2.y, lc.handle);
      if (s.cx != null) {
        const cp = this.w2s(s.cx, s.cy);
        ctx.setLineDash([4, 3]);
        ctx.strokeStyle = lc.ctrl;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(p1.x, p1.y); ctx.lineTo(cp.x, cp.y);
        ctx.moveTo(p2.x, p2.y); ctx.lineTo(cp.x, cp.y);
        ctx.stroke();
        ctx.setLineDash([]);
        this.drawHandle(cp.x, cp.y, lc.ctrl);
      }
    }
  }

  isStartCp(cp) {
    return this.cps.length > 0 && cp.id === Math.min(...this.cps.map(c => c.id));
  }

  // A terrain area: the ground the car will stand on there, drawn as a translucent fill in its
  // terrain's colour so the road and the background still read through it. Selected, its nodes (and
  // the handles of the selected one) show — exactly like a road area's, the shapes edit the same way.
  drawTerrain(area, sel, hov) {
    const ctx = this.ctx;
    const points = this.shapePoints(area, true);
    if (points.length < 3) return;
    const spts = points.map(p => this.w2s(p.x, p.y));
    const colour = terrainColor(terrainOf(area));

    ctx.save();
    ctx.globalAlpha *= sel ? 0.55 : hov ? 0.45 : 0.35;
    ctx.beginPath();
    ctx.moveTo(spts[0].x, spts[0].y);
    for (let i = 1; i < spts.length; i++) ctx.lineTo(spts[i].x, spts[i].y);
    ctx.closePath();
    ctx.fillStyle = colour;
    ctx.fill();
    ctx.restore();

    ctx.strokeStyle = sel ? '#60a5fa' : hov ? '#9ca3af' : colour;
    ctx.lineWidth = sel ? 2 : hov ? 1.5 : 1;
    ctx.stroke();

    if (sel) this.drawNodeHandles(area, '#60a5fa', '#a78bfa');
  }

  // A road area: the free road shape, drawn as the asphalt it will be in game — a filled polygon with
  // its outline, and, when selected, its nodes plus the handles of the selected one.
  drawArea(area, sel, hov) {
    const ctx = this.ctx;
    const points = this.shapePoints(area, true);
    if (points.length < 3) return;
    const spts = points.map(p => this.w2s(p.x, p.y));

    ctx.beginPath();
    ctx.moveTo(spts[0].x, spts[0].y);
    for (let i = 1; i < spts.length; i++) ctx.lineTo(spts[i].x, spts[i].y);
    ctx.closePath();
    ctx.fillStyle = sel ? 'rgba(30,58,95,0.85)' : hov ? 'rgba(45,55,72,0.8)' : 'rgba(31,41,55,0.7)';
    ctx.strokeStyle = sel ? '#60a5fa' : hov ? '#9ca3af' : '#4b5563';
    ctx.lineWidth = sel ? 2 : hov ? 1.5 : 1;
    ctx.fill();
    ctx.stroke();

    // A perimeter closes on its own from the last node back to the first, and that side was never
    // drawn by hand: it is shown dashed. A long dashed side cutting across the shape is exactly what
    // a perimeter that was never brought back to its start looks like.
    const nodes = area.points ?? [];
    const closing = this.sampleEdge(this.edgeControls(nodes, nodes.length - 1, true));
    if (closing.length >= 2) {
      ctx.save();
      ctx.globalAlpha = 0.75;
      ctx.setLineDash([6, 4]);
      ctx.strokeStyle = '#22d3ee';
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      const first = this.w2s(closing[0].x, closing[0].y);
      ctx.moveTo(first.x, first.y);
      for (let i = 1; i < closing.length; i++) {
        const p = this.w2s(closing[i].x, closing[i].y);
        ctx.lineTo(p.x, p.y);
      }
      ctx.stroke();
      ctx.restore();
    }

    if (sel) this.drawNodeHandles(area, '#60a5fa', '#a78bfa');
  }

  /**
   * All the nodes of an area/polygon, plus the two Bezier handles of the selected node (with dashed
   * lines out to them): that is where the curve is shaped. Handles still sitting on the "straight
   * line" default are drawn faint — dragging one is what turns the edge into a curve.
   */
  drawNodeHandles(obj, nodeColor, handleColor) {
    const ctx = this.ctx;
    const nodes = obj.points ?? [];
    const selected = this.selNode;
    const node = selected != null ? nodes[selected] : null;

    if (node) {
      const h = this.nodeHandles(nodes, selected, !!obj.closed);
      for (const [key, point] of [['out', h.out], ['in', h.in]]) {
        if (!point) continue;
        const real = key === 'out' ? !!node.hOut : !!node.hIn;
        const a = this.w2s(node.x, node.y), b = this.w2s(point.x, point.y);
        ctx.save();
        ctx.globalAlpha = real ? 0.9 : 0.45;
        ctx.setLineDash([4, 3]);
        ctx.strokeStyle = handleColor;
        ctx.lineWidth = 1;
        ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y); ctx.stroke();
        ctx.restore();
        this.drawHandle(b.x, b.y, handleColor, false, !real);
      }
    }

    for (let i = 0; i < nodes.length; i++) {
      const p = this.w2s(nodes[i].x, nodes[i].y);
      this.drawHandle(p.x, p.y, nodeColor);
      if (i === selected) {
        ctx.beginPath();
        ctx.arc(p.x, p.y, HR - 3, 0, Math.PI * 2);
        ctx.fillStyle = nodeColor;
        ctx.fill();
      }
    }
  }

  drawCp(cp, sel, hov) {
    const ctx = this.ctx;
    const p1 = this.w2s(cp.x1, cp.y1), p2 = this.w2s(cp.x2, cp.y2);
    const start = this.isStartCp(cp);

    // Timing points (the start/finish line always counts as one) get a colour of their own: they
    // are where the game shows the sector deltas.
    const color = start ? '#ef4444' : cp.timing ? '#22d3ee' : sel ? '#fbbf24' : hov ? '#fcd34d' : '#f59e0b';

    if (start) {
      const len = Math.hypot(p2.x - p1.x, p2.y - p1.y);
      const steps = Math.max(6, Math.floor(len / 8));
      for (let i = 0; i < steps; i++) {
        const ta = i / steps, tb = (i + 1) / steps;
        ctx.strokeStyle = i % 2 === 0 ? '#ef4444' : '#ffffff';
        ctx.lineWidth = sel ? 4 : 3;
        ctx.beginPath();
        ctx.moveTo(p1.x + (p2.x - p1.x) * ta, p1.y + (p2.y - p1.y) * ta);
        ctx.lineTo(p1.x + (p2.x - p1.x) * tb, p1.y + (p2.y - p1.y) * tb);
        ctx.stroke();
      }
    } else {
      ctx.setLineDash([8, 4]);
      ctx.strokeStyle = color;
      ctx.lineWidth = sel ? 3 : 2;
      ctx.beginPath(); ctx.moveTo(p1.x, p1.y); ctx.lineTo(p2.x, p2.y); ctx.stroke();
      ctx.setLineDash([]);
    }

    const lx = (p1.x + p2.x) / 2, ly = (p1.y + p2.y) / 2;
    ctx.font = 'bold 11px system-ui, sans-serif';
    ctx.fillStyle = color;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(start ? 'S/F' : cp.timing ? `CP${cp.id} • timing` : `CP${cp.id}`, lx, ly - 12);

    if (sel) {
      this.drawHandle(p1.x, p1.y, color);
      this.drawHandle(p2.x, p2.y, color);
      this.drawHandle(lx, ly, '#34d399', true);
    }
  }

  drawSg(sg, sel, hov) {
    const ctx = this.ctx;
    const p1 = this.w2s(sg.x1, sg.y1), p2 = this.w2s(sg.x2, sg.y2);
    const dx = sg.x2 - sg.x1, dy = sg.y2 - sg.y1;
    const len = Math.hypot(dx, dy);
    if (len < 0.01) return;
    const color = sel ? '#34d399' : hov ? '#6ee7b7' : '#10b981';
    const mx = (p1.x + p2.x) / 2, my = (p1.y + p2.y) / 2;
    const angle = sg.angle;
    const nx = Math.cos(angle), ny = Math.sin(angle);

    const cxw = (sg.x1 + sg.x2) / 2, cyw = (sg.y1 + sg.y2) / 2;
    const gridPts = buildStartGrid({ x: cxw, y: cyw }, angle, 8, sg.staggerPx ?? 20, !!sg.flip);
    ctx.font = '9px system-ui, sans-serif';
    for (let i = 0; i < gridPts.length; i++) {
      const gp = this.w2s(gridPts[i].x, gridPts[i].y);
      ctx.beginPath();
      ctx.arc(gp.x, gp.y, 4, 0, Math.PI * 2);
      ctx.fillStyle = color + '55';
      ctx.strokeStyle = color + 'aa';
      ctx.lineWidth = 1;
      ctx.fill();
      ctx.stroke();
      ctx.fillStyle = color;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'bottom';
      ctx.fillText(i + 1, gp.x, gp.y - 4);
    }

    ctx.setLineDash([]);
    ctx.strokeStyle = color;
    ctx.lineWidth = sel ? 3 : 2;
    ctx.beginPath(); ctx.moveTo(p1.x, p1.y); ctx.lineTo(p2.x, p2.y); ctx.stroke();

    const arrowLen = 40;
    const ex = mx + nx * arrowLen, ey = my + ny * arrowLen;
    const aSize = 8;
    ctx.strokeStyle = color;
    ctx.fillStyle = color;
    ctx.lineWidth = 2;
    ctx.beginPath(); ctx.moveTo(mx, my); ctx.lineTo(ex, ey); ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(ex, ey);
    ctx.lineTo(ex - Math.cos(angle - 0.5) * aSize, ey - Math.sin(angle - 0.5) * aSize);
    ctx.lineTo(ex - Math.cos(angle + 0.5) * aSize, ey - Math.sin(angle + 0.5) * aSize);
    ctx.closePath();
    ctx.fill();

    ctx.font = 'bold 11px system-ui, sans-serif';
    ctx.fillStyle = color;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('START', mx, my - 14);

    if (sel) {
      this.drawHandle(p1.x, p1.y, color);
      this.drawHandle(p2.x, p2.y, color);
      this.drawHandle(mx, my, '#34d399', true);
    }
  }

  drawPit(p, sel, hov) {
    const ctx = this.ctx;
    const px1 = Math.min(p.x1, p.x2), px2 = Math.max(p.x1, p.x2);
    const py1 = Math.min(p.y1, p.y2), py2 = Math.max(p.y1, p.y2);
    const tl = this.w2s(px1, py1), br = this.w2s(px2, py2);
    const sw = br.x - tl.x, sh = br.y - tl.y;
    if (sw < 1 || sh < 1) return;

    const color = sel ? '#34d399' : hov ? '#6ee7b7' : '#10b981';

    ctx.fillStyle = `rgba(16,185,129,${sel ? 0.15 : hov ? 0.1 : 0.06})`;
    ctx.strokeStyle = color;
    ctx.lineWidth = sel ? 2 : 1.5;
    ctx.setLineDash([]);
    ctx.fillRect(tl.x, tl.y, sw, sh);
    ctx.strokeRect(tl.x, tl.y, sw, sh);

    // The label sits at the top of the box, leaving the middle to the facing arrow below.
    ctx.font = `bold ${Math.max(11, Math.min(28, sh * 0.4))}px system-ui, sans-serif`;
    ctx.fillStyle = color;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('PIT', tl.x + sw / 2, tl.y + Math.min(sh / 2, Math.max(12, sh * 0.25)));

    // The line the pit crew walks out of: `crewOffset` px OUTSIDE the box's crew side, so the
    // mechanics appear past the box's opening instead of exactly on its border (see the game's
    // rendering/pitCrew). Drawn in red so it doesn't read as part of the pit box itself.
    const off = Math.max(0, p.crewOffset ?? DEFAULT_CREW_OFFSET);
    const line = {
      left:   { x1: px1 - off, y1: py1, x2: px1 - off, y2: py2 },
      right:  { x1: px2 + off, y1: py1, x2: px2 + off, y2: py2 },
      top:    { x1: px1, y1: py1 - off, x2: px2, y2: py1 - off },
      bottom: { x1: px1, y1: py2 + off, x2: px2, y2: py2 + off },
    }[this.pitCrewSide()];
    const la = this.w2s(line.x1, line.y1), lb = this.w2s(line.x2, line.y2);
    ctx.strokeStyle = '#ef4444';
    ctx.lineWidth = 2;
    ctx.setLineDash([6, 4]);
    ctx.beginPath();
    ctx.moveTo(la.x, la.y);
    ctx.lineTo(lb.x, lb.y);
    ctx.stroke();
    ctx.setLineDash([]);

    // Which way the cars parked here will face (see pitFacing): the one thing about a pit box that
    // cannot be judged from the drawing, since the game reads it off the lap — so it is drawn, and
    // this is also what the `direction` option is chosen against. Painted twice, a dark outline
    // under the white, so it reads on the dark canvas and on a light background image alike.
    const facing = this.pitFacing();
    const fx = tl.x + sw / 2, fy = tl.y + sh / 2;
    const flen = Math.max(14, Math.min(sw, sh) * 0.3);
    const ex = fx + Math.cos(facing) * flen, ey = fy + Math.sin(facing) * flen;
    for (const [width, style] of [[5, '#0d1117'], [2, '#f8fafc']]) {
      ctx.strokeStyle = style;
      ctx.fillStyle = style;
      ctx.lineWidth = width;
      ctx.beginPath();
      ctx.moveTo(fx - Math.cos(facing) * flen * 0.5, fy - Math.sin(facing) * flen * 0.5);
      ctx.lineTo(ex, ey);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(ex, ey);
      ctx.lineTo(ex - Math.cos(facing - 0.5) * 7, ey - Math.sin(facing - 0.5) * 7);
      ctx.lineTo(ex - Math.cos(facing + 0.5) * 7, ey - Math.sin(facing + 0.5) * 7);
      ctx.closePath();
      ctx.fill();
    }

    if (sel) {
      const hp1 = this.w2s(p.x1, p.y1), hp2 = this.w2s(p.x2, p.y2);
      this.drawHandle(hp1.x, hp1.y, color);
      this.drawHandle(hp2.x, hp2.y, color);
      this.drawHandle(tl.x + sw / 2, tl.y + sh / 2, '#34d399', true);
    }
  }

  drawObstacle(o, sel, hov) {
    if (o.type === 'circle') this.drawObsCircle(o, sel, hov);
    else if (o.type === 'rect') this.drawObsRect(o, sel, hov);
    else if (o.type === 'poly') this.drawObsPoly(o, sel, hov);
    else this.drawObsWall(o, sel, hov);
  }

  /**
   * The pictures the engine is about to draw but doesn't have yet. The host fetches them and calls
   * setMaterialImages — one ask per src, ever (a src that failed to load stays missing and the shape
   * falls back to its flat colour).
   */
  requestImage(src) {
    if (!this.onImages || this.imagesRequested.has(src)) return;
    this.imagesRequested.add(src);
    this.onImages([src]);
  }

  /** Hands over sprites by src (`{ 'materials/stands/1.png': HTMLImageElement }`). */
  setMaterialImages(images) {
    for (const [src, image] of Object.entries(images ?? {})) this.materialImages.set(src, image);
    this.tyreSets.clear(); // a tyre sheet arriving is what every measured set was waiting for
    this.render();
  }

  /**
   * The tiled surface of a material that IS one (a plain wall's photo), ready to fill or stroke a shape,
   * or null when the material has no such picture — either because it is drawn some other way or because
   * the picture is still on its way (in which case it is asked for here).
   *
   * The tile is the picture drawn to WALL_TILE world units, the size the painter repeats it at, and the
   * pattern is anchored to the world: the editor then shows the wall made of the same surface the game
   * will paint, at any zoom.
   */
  wallTexture(material) {
    const look = MATERIALS[material];
    const src = look?.image?.src;
    // Only a plain surface is tiled here. A sprite, a tyre pile or a fence carries its own detail and is
    // drawn by its own pass (drawRectSprite / drawTyreObstacle / drawFenceObstacle).
    if (!src || !this.createCanvas || look.unit || look.strip || look.composite || look.variants) return null;
    const image = this.materialImages.get(src);
    if (!image) {
      this.requestImage(src);
      return null;
    }
    let pattern = this._wallTextures.get(src);
    if (!pattern) {
      const tile = this.createCanvas(WALL_TILE, WALL_TILE);
      tile.getContext('2d').drawImage(image, 0, 0, WALL_TILE, WALL_TILE);
      pattern = this.ctx.createPattern(tile, 'repeat');
      this._wallTextures.set(src, pattern);
    }
    // Re-anchored every draw, so the grain sits on the world and does not slide under the shape as the
    // view is panned or zoomed.
    if (pattern?.setTransform) {
      pattern.setTransform({ a: this.zoom, b: 0, c: 0, d: this.zoom, e: -this.panX * this.zoom, f: -this.panY * this.zoom });
    }
    return pattern;
  }

  /** Is this material painted as whole tyres? (materials.js: a look with a `unit`.) */
  isTyreMaterial(name) {
    return Boolean(MATERIALS[name]?.unit);
  }

  /**
   * The material's tyres, ready to stamp, or null while its sheet is on its way (the engine asks the host
   * for it through requestImage and the answer arrives in setMaterialImages, same as the sprites).
   */
  tyreSetFor(name) {
    const ready = this.tyreSets.get(name);
    if (ready) return ready;
    const look = MATERIALS[name];
    const src = look?.image?.src;
    const image = src ? this.materialImages.get(src) : null;
    if (src && !image) {
      this.requestImage(src);
      return null;
    }
    const set = image ? tyreSet(look, image, this.createCanvas) : null;
    if (set) this.tyreSets.set(name, set);
    return set;
  }

  /**
   * One obstacle's tyres, in the places the game puts them: the layout is the game's own (tyreLayout.js,
   * the module the painter fills its piles with), so what the editor shows is what the track comes out
   * like. Only whole stacks that fit the shape are drawn; leftover space stays empty.
   */
  drawTyreObstacle(o) {
    if (!this.isTyreMaterial(o.material)) return false;
    const set = this.tyreSetFor(o.material);
    if (!set) return true; // a tyre material whose sheet hasn't arrived: nothing to draw yet
    // Sample curved polygons just as export/Preview does, rather than using their control nodes.
    const shape = o.type === 'poly' ? { ...o, points: this.shapePoints(o, false) } : o;
    const draws = tyreStampsForObstacle(shape, set).map(stamp => ({ stamp, set }));
    if (this._tyreDrawQueue) this._tyreDrawQueue.push(...draws);
    else this.drawTyreStacks(draws);
    return true;
  }

  drawTyreStacks(draws) {
    if (!draws.length) return;
    const ctx = this.ctx;
    ctx.save();
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    draws.sort((a, b) => compareTyreDepth(a.stamp, b.stamp));
    for (const { stamp, set } of draws) {
      ctx.save();
      ctx.translate((stamp.x - this.panX) * this.zoom, (stamp.y - this.panY) * this.zoom);
      ctx.scale(this.zoom, this.zoom);
      drawTyreCell(ctx, set, stamp);
      ctx.restore();
    }
    ctx.restore();
  }

  drawFenceObstacle(o) {
    const look = MATERIALS[o.material];
    if (look?.strip !== 'fence') return;
    const image = this.materialImages.get(look.image.src);
    if (!image) this.requestImage(look.image.src);
    const points = o.type === 'poly' ? this.shapePoints(o, false)
      : [{ x: o.x1, y: o.y1 }, { x: o.x2, y: o.y2 }];
    const ctx = this.ctx;
    ctx.save();
    ctx.scale(this.zoom, this.zoom);
    ctx.translate(-this.panX, -this.panY);
    drawFence(ctx, points, o.thickness ?? (o.type === 'poly' ? this.defPolyThickness : this.defObsThickness), image, look.plinth);
    ctx.restore();
  }

  obsColors(sel, hov) {
    return {
      fill: sel ? '#5c1414' : hov ? '#4a1010' : '#3a0d0d',
      stroke: sel ? '#fca5a5' : hov ? '#f87171' : OBS_COLOR,
    };
  }

  drawObsWall(o, sel, hov) {
    const ctx = this.ctx;
    const fake = { x1: o.x1, y1: o.y1, x2: o.x2, y2: o.y2, width: o.thickness };
    const { left, right, center } = this.segRibbon(fake);
    if (left.length < 2) return;
    const p1 = center[0], p2 = center[center.length - 1];
    const { fill, stroke } = this.obsColors(sel, hov);
    const tyre = this.isTyreMaterial(o.material);
    const fence = MATERIALS[o.material]?.strip === 'fence';
    // A wall made of a textured material (concrete, and its variants) shows that surface rather than the
    // flat colour; the outline still marks exactly where the wall is.
    const texture = (!tyre && !fence) ? this.wallTexture(o.material) : null;

    ctx.beginPath();
    ctx.moveTo(left[0].x, left[0].y);
    for (let i = 1; i < left.length; i++) ctx.lineTo(left[i].x, left[i].y);
    for (let i = right.length - 1; i >= 0; i--) ctx.lineTo(right[i].x, right[i].y);
    ctx.closePath();
    // A wall of tyres is the tyres: the slab of colour goes, so that what shows between them is the
    // canvas (in game it is the ground under the pile).
    ctx.fillStyle = tyre ? 'rgba(0,0,0,0)' : (texture ?? fill);
    ctx.strokeStyle = stroke;
    ctx.lineWidth = sel ? 2 : hov ? 1.5 : 1;
    if (!tyre && !fence) ctx.fill();
    if (!fence || sel || hov) ctx.stroke();
    this.drawTyreObstacle(o);
    this.drawFenceObstacle(o);

    if (sel) {
      this.drawHandle(p1.x, p1.y, OBS_COLOR);
      this.drawHandle(p2.x, p2.y, OBS_COLOR);
      const mx = (p1.x + p2.x) / 2, my = (p1.y + p2.y) / 2;
      this.drawHandle(mx, my, '#fca5a5', true);
    }
  }

  drawObsCircle(o, sel, hov) {
    const ctx = this.ctx;
    const c = this.w2s(o.x, o.y);
    const r = o.radius * this.zoom;
    if (r < 0.5) return;
    const { fill, stroke } = this.obsColors(sel, hov);
    const tyre = this.isTyreMaterial(o.material);
    // A circle of a textured material (a plain wall) shows its surface, like the wall and the rectangle.
    const texture = tyre ? null : this.wallTexture(o.material);

    ctx.beginPath();
    ctx.arc(c.x, c.y, r, 0, Math.PI * 2);
    ctx.fillStyle = tyre ? 'rgba(0,0,0,0)' : (texture ?? fill);
    ctx.strokeStyle = stroke;
    ctx.lineWidth = sel ? 2 : hov ? 1.5 : 1;
    if (!tyre) ctx.fill();
    this.drawTyreObstacle(o);
    ctx.stroke();

    if (sel) {
      this.drawHandle(c.x, c.y, '#fca5a5', true);
      const edge = this.w2s(o.x + o.radius, o.y);
      this.drawHandle(edge.x, edge.y, OBS_COLOR);
    }
  }

  drawObsRect(o, sel, hov) {
    const ctx = this.ctx;
    const hw = o.width / 2, hh = o.height / 2;
    const cos = Math.cos(o.angle ?? 0), sin = Math.sin(o.angle ?? 0);
    const local = [[-hw, -hh], [hw, -hh], [hw, hh], [-hw, hh]]
      .map(([x, y]) => this.w2s(o.x + x * cos - y * sin, o.y + x * sin + y * cos));
    const { fill, stroke } = this.obsColors(sel, hov);
    // A sprite material is filled with its own ground colour, not the obstacle red: its picture is a shape
    // seen at an angle, so its corners are transparent and in game the material's `base` is what shows
    // there (see paintBlockStrip in ./trackPainter.js). The red outline still marks the exact
    // rectangle. A material made of tyres isn't filled at all — the tyres are the whole of it.
    const sprite = materialVariant(o.material, o.variant ?? 0);
    const tyre = this.isTyreMaterial(o.material);
    // A textured material (a plain wall) shows its surface instead of the flat colour. A material that is
    // one sprite (houses/signals) or a stand keeps its own ground colour, drawn by drawRectSprite.
    const texture = (!tyre && !sprite) ? this.wallTexture(o.material) : null;

    ctx.beginPath();
    ctx.moveTo(local[0].x, local[0].y);
    for (let i = 1; i < local.length; i++) ctx.lineTo(local[i].x, local[i].y);
    ctx.closePath();
    ctx.fillStyle = tyre ? 'rgba(0,0,0,0)' : sprite ? (MATERIALS[o.material]?.base ?? fill) : (texture ?? fill);
    ctx.strokeStyle = stroke;
    ctx.lineWidth = sel ? 2 : hov ? 1.5 : 1;
    if (!tyre) ctx.fill();
    // The material's picture goes in before the outline: a rectangle that is one sprite fitted to it is
    // better judged from the sprite than from a flat colour, and the outline stays on top of it so the
    // shape (and its handles) are still what you select. The path is still open for clipping here — the
    // sprite is fit "cover", so most of it falls outside and the rectangle's edge is what crops it, just
    // as the game's painter does (see paintBlockStrip). A material made of tyres is drawn by the tyre
    // layout instead, which needs no clipping: the pile reaches the ends and stays inside the thickness.
    ctx.save();
    if (!tyre) ctx.clip();
    if (!this.drawTyreObstacle(o)) this.drawRectSprite(o);
    ctx.restore();
    ctx.stroke();

    if (sel) {
      const center = this.w2s(o.x, o.y);
      this.drawHandle(center.x, center.y, '#fca5a5', true);
      const corner = this.w2s(this.rectCorner(o).x, this.rectCorner(o).y);
      this.drawHandle(corner.x, corner.y, OBS_COLOR);
      const spin = this.rectSpinHandle(o);
      const top = this.w2s(o.x + hh * sin, o.y - hh * cos);
      const spinScreen = this.w2s(spin.x, spin.y);
      ctx.save();
      ctx.strokeStyle = '#34d399';
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(top.x, top.y);
      ctx.lineTo(spinScreen.x, spinScreen.y);
      ctx.stroke();
      ctx.restore();
      this.drawHandle(spinScreen.x, spinScreen.y, '#34d399');
    }
  }

  /**
   * The sprite of a material that IS one (a stand), scaled to the rectangle the way the painter scales
   * it: `length`/`depth` are the world size the sprite stands for, so the scale is the larger of the two
   * the rectangle would need ("cover") — the sprite is never stretched, only cropped by the shape. Must
   * stay in step with paintBlockStrip in ./trackPainter.js or the editor would lie about how
   * the stand lands.
   */
  drawRectSprite(o) {
    const look = MATERIALS[o.material];
    if (look?.composite === 'stand') {
      const image = this.materialImages.get(look.image.src);
      if (!image) this.requestImage(look.image.src);
      const center = this.w2s(o.x, o.y);
      this.ctx.save();
      this.ctx.translate(center.x, center.y);
      this.ctx.rotate(o.angle ?? 0);
      this.ctx.scale(this.zoom, this.zoom);
      drawStand(this.ctx, o.width, o.height, image, this.createCanvas, o.id ?? 0);
      this.ctx.restore();
      return;
    }
    const variant = materialVariant(o.material, o.variant ?? 0);
    if (!variant) return;
    const sprite = this.materialImages.get(variant.src);
    if (!sprite) {
      this.requestImage(variant.src);
      return;
    }
    const scale = Math.max(o.width / variant.length, o.height / variant.depth) * this.zoom;
    const w = variant.length * scale, h = variant.depth * scale;
    const c = this.w2s(o.x, o.y);
    const ctx = this.ctx;
    ctx.save();
    // Sprites are drawn at a fraction of their size: the default filter turns a crowd into grey mush.
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.translate(c.x, c.y);
    ctx.rotate(o.angle ?? 0);
    ctx.drawImage(sprite, -w / 2, -h / 2, w, h);
    ctx.restore();
  }

  // A polygon obstacle is a wall of `thickness` following the node polyline (its curves sampled, so
  // a curved edge is a curved wall). Drawn as ONE stroked path (round joins/caps) rather than one
  // quad per edge, so corners stay seamless; the outline is a wider stroke underneath the body.
  drawObsPoly(o, sel, hov) {
    const ctx = this.ctx;
    const world = this.shapePoints(o, false);
    if (world.length < 2) return;
    const spts = world.map(p => this.w2s(p.x, p.y));
    const { fill, stroke } = this.obsColors(sel, hov);
    const w = Math.max(2, (o.thickness ?? this.defPolyThickness) * this.zoom);
    const tyre = this.isTyreMaterial(o.material);
    const fence = MATERIALS[o.material]?.strip === 'fence';
    // A textured poly wall (a plain wall's material) strokes its body with that surface.
    const texture = (!tyre && !fence) ? this.wallTexture(o.material) : null;

    ctx.save();
    ctx.setLineDash([]);
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(spts[0].x, spts[0].y);
    for (let i = 1; i < spts.length; i++) ctx.lineTo(spts[i].x, spts[i].y);
    ctx.strokeStyle = stroke;
    ctx.lineWidth = w + 2;
    if (fence) ctx.globalAlpha = 0.2;
    if (!fence || sel || hov) ctx.stroke();
    if (!tyre && !fence) {
      ctx.strokeStyle = texture ?? fill;
      ctx.lineWidth = w;
      ctx.stroke();
    }
    ctx.restore();
    // The tyres are drawn after the outline: the pile sits in the strip, and the outline is what marks
    // where the strip actually is (the tyres only reach it).
    this.drawTyreObstacle(o);
    this.drawFenceObstacle(o);

    if (sel) {
      this.drawNodeHandles(o, OBS_COLOR, '#f87171');
      const c = this.polylineCenter(o);
      const sc = this.w2s(c.x, c.y);
      this.drawHandle(sc.x, sc.y, '#fca5a5', true);
    }
  }

  drawHandle(x, y, strokeColor, isMid = false, faint = false) {
    const ctx = this.ctx;
    ctx.save();
    if (faint) ctx.globalAlpha = 0.5;
    ctx.beginPath();
    ctx.arc(x, y, HR, 0, Math.PI * 2);
    ctx.fillStyle = '#0d1117';
    ctx.strokeStyle = strokeColor;
    ctx.lineWidth = 2;
    ctx.fill();
    ctx.stroke();
    ctx.restore();

    if (isMid) {
      ctx.strokeStyle = strokeColor;
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(x - 3, y); ctx.lineTo(x + 3, y);
      ctx.moveTo(x, y - 3); ctx.lineTo(x, y + 3);
      ctx.stroke();
    }
  }

  drawSnapIndicator(pt) {
    const ctx = this.ctx;
    const sp = this.w2s(pt.x, pt.y);
    ctx.beginPath();
    ctx.arc(sp.x, sp.y, HR + 6, 0, Math.PI * 2);
    ctx.strokeStyle = '#34d399';
    ctx.lineWidth = 2.5;
    ctx.setLineDash([5, 3]);
    ctx.stroke();
    ctx.setLineDash([]);
  }

  drawPreview() {
    const ctx = this.ctx;
    const isObs = this.mode === 'addWall' || this.mode === 'addCircle' || this.mode === 'addRect' || this.mode === 'addPoly';
    const color = this.mode === 'addSeg' ? '#60a5fa' : this.mode === 'addRoad' ? '#60a5fa'
      : this.mode === 'addSg' ? '#10b981' : this.mode === 'addPit' ? '#10b981' : isObs ? OBS_COLOR : '#f59e0b';
    const ms = this.w2s(this.mw.x, this.mw.y);

    if (this.mode === 'addPoly' || this.mode === 'addRoad' || this.mode === 'addTerrainArea') {
      const isArea = this.mode !== 'addPoly';
      this.drawDraft(ms, isArea);
      this.drawHandle(ms.x, ms.y, isArea ? '#60a5fa' : OBS_COLOR);
      return;
    }

    if (this.addFirst) {
      const p1 = this.w2s(this.addFirst.x, this.addFirst.y);

      if (this.mode === 'addSeg') {
        const fake = { x1: this.addFirst.x, y1: this.addFirst.y, x2: this.mw.x, y2: this.mw.y, width: this.defWidth };
        const { left, right } = this.segRibbon(fake);
        if (left.length >= 2) {
          ctx.beginPath();
          ctx.moveTo(left[0].x, left[0].y);
          for (let i = 1; i < left.length; i++) ctx.lineTo(left[i].x, left[i].y);
          for (let i = right.length - 1; i >= 0; i--) ctx.lineTo(right[i].x, right[i].y);
          ctx.closePath();
          ctx.fillStyle = 'rgba(96,165,250,0.12)';
          ctx.strokeStyle = '#60a5fa';
          ctx.lineWidth = 1.5;
          ctx.setLineDash([5, 3]);
          ctx.fill();
          ctx.stroke();
          ctx.setLineDash([]);
        }
      } else if (this.mode === 'addWall') {
        const fake = { x1: this.addFirst.x, y1: this.addFirst.y, x2: this.mw.x, y2: this.mw.y, width: this.defObsThickness };
        const { left, right } = this.segRibbon(fake);
        if (left.length >= 2) {
          ctx.beginPath();
          ctx.moveTo(left[0].x, left[0].y);
          for (let i = 1; i < left.length; i++) ctx.lineTo(left[i].x, left[i].y);
          for (let i = right.length - 1; i >= 0; i--) ctx.lineTo(right[i].x, right[i].y);
          ctx.closePath();
          ctx.fillStyle = 'rgba(220,38,38,0.15)';
          ctx.strokeStyle = OBS_COLOR;
          ctx.lineWidth = 1.5;
          ctx.setLineDash([5, 3]);
          ctx.fill();
          ctx.stroke();
          ctx.setLineDash([]);
        }
      } else if (this.mode === 'addCircle') {
        const r = Math.hypot(this.mw.x - this.addFirst.x, this.mw.y - this.addFirst.y) * this.zoom;
        ctx.beginPath();
        ctx.arc(p1.x, p1.y, r, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(220,38,38,0.12)';
        ctx.strokeStyle = OBS_COLOR;
        ctx.lineWidth = 1.5;
        ctx.setLineDash([5, 3]);
        ctx.fill();
        ctx.stroke();
        ctx.setLineDash([]);
      } else if (this.mode === 'addTerrainCircle') {
        const r = Math.hypot(this.mw.x - this.addFirst.x, this.mw.y - this.addFirst.y) * this.zoom;
        ctx.beginPath();
        ctx.arc(p1.x, p1.y, r, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(169,121,63,0.18)';
        ctx.strokeStyle = terrainColor(this.defTerrain);
        ctx.lineWidth = 1.5;
        ctx.setLineDash([5, 3]);
        ctx.fill();
        ctx.stroke();
        ctx.setLineDash([]);
      } else if (this.mode === 'addTerrainRect') {
        const tlx = Math.min(p1.x, ms.x), tly = Math.min(p1.y, ms.y);
        const brx = Math.max(p1.x, ms.x), bry = Math.max(p1.y, ms.y);
        ctx.fillStyle = 'rgba(169,121,63,0.18)';
        ctx.strokeStyle = terrainColor(this.defTerrain);
        ctx.lineWidth = 1.5;
        ctx.setLineDash([5, 3]);
        ctx.fillRect(tlx, tly, brx - tlx, bry - tly);
        ctx.strokeRect(tlx, tly, brx - tlx, bry - tly);
        ctx.setLineDash([]);
      } else if (this.mode === 'addPit' || this.mode === 'addRect') {
        const tlx = Math.min(p1.x, ms.x), tly = Math.min(p1.y, ms.y);
        const brx = Math.max(p1.x, ms.x), bry = Math.max(p1.y, ms.y);
        ctx.fillStyle = this.mode === 'addPit' ? 'rgba(16,185,129,0.1)' : 'rgba(220,38,38,0.12)';
        ctx.strokeStyle = this.mode === 'addPit' ? '#10b981' : OBS_COLOR;
        ctx.lineWidth = 1.5;
        ctx.setLineDash([5, 3]);
        ctx.fillRect(tlx, tly, brx - tlx, bry - tly);
        ctx.strokeRect(tlx, tly, brx - tlx, bry - tly);
        ctx.setLineDash([]);
      } else {
        ctx.setLineDash([8, 4]);
        ctx.strokeStyle = color;
        ctx.lineWidth = 2;
        ctx.beginPath(); ctx.moveTo(p1.x, p1.y); ctx.lineTo(ms.x, ms.y); ctx.stroke();
        ctx.setLineDash([]);
      }

      this.drawHandle(p1.x, p1.y, color);
    }

    this.drawHandle(ms.x, ms.y, color);
  }

  // Live preview while drawing a polygon wall or a road area: the committed nodes plus a rubber band
  // from the last one to the cursor (or to the magnet point, when one is in range). A road area is
  // shown as the surface it will be — a filled shape closed back on its first node. When the draft
  // continues an existing polygon, its anchor node is drawn as the first point.
  drawDraft(ms, isArea) {
    const anchor = this.extendAnchor();
    const own = this.draft ?? [];
    const pts = anchor ? [anchor, ...own] : own;
    if (!pts.length) return;
    const ctx = this.ctx;
    const end = this.snapTarget ? this.w2s(this.snapTarget.x, this.snapTarget.y) : ms;
    const color = isArea ? '#60a5fa' : OBS_COLOR;
    const ring = pts.map(p => this.w2s(p.x, p.y));

    ctx.save();
    ctx.globalAlpha = isArea ? 0.75 : 0.8;
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    ctx.strokeStyle = color;
    ctx.lineWidth = isArea ? 1.5 : Math.max(1.5, this.defPolyThickness * this.zoom);
    ctx.setLineDash(isArea ? [5, 3] : []);
    ctx.beginPath();
    ctx.moveTo(ring[0].x, ring[0].y);
    for (let i = 1; i < ring.length; i++) ctx.lineTo(ring[i].x, ring[i].y);
    ctx.lineTo(end.x, end.y);
    ctx.stroke();
    if (isArea && ring.length >= 2) {
      // The closing side is part of the shape but not drawn yet: shown dashed while drawing, so it
      // is clear that the perimeter will run from the cursor back to the first node.
      ctx.save();
      ctx.setLineDash([6, 4]);
      ctx.strokeStyle = '#22d3ee';
      ctx.lineWidth = 1.2;
      ctx.beginPath();
      ctx.moveTo(end.x, end.y);
      ctx.lineTo(ring[0].x, ring[0].y);
      ctx.stroke();
      ctx.restore();
      ctx.beginPath();
      ctx.moveTo(ring[0].x, ring[0].y);
      for (let i = 1; i < ring.length; i++) ctx.lineTo(ring[i].x, ring[i].y);
      ctx.lineTo(end.x, end.y);
      ctx.closePath();
      ctx.fillStyle = 'rgba(30,58,95,0.5)';
      ctx.fill();
    }
    ctx.restore();

    for (const p of ring) this.drawHandle(p.x, p.y, color);
  }

  // ── Events ───────────────────────────────────────────────────
  onDown(e) {
    e.preventDefault();
    const sp = this.mousePos(e);
    const wp = this.s2w(sp.x, sp.y);
    // A drag moves the road itself, which no notify() announces (see onMove): the magnet's samples are
    // taken again when the gesture starts and when it ends.
    this.invalidateRoadSamples();

    if (e.button === 1) {
      this.panState = { ax: sp.x, ay: sp.y, px: this.panX, py: this.panY };
      this.canvas.style.cursor = 'grabbing';
      return;
    }
    if (e.button !== 0) return;

    if (this.mode === 'select') {
      const hit = this.hitTest(wp.x, wp.y);
      if (hit) {
        const sameObject = this.selected && this.selected.type === hit.type && this.selected.id === hit.id;
        if (!sameObject) this.setSelected(hit.type, hit.id);
        // `n3` = node 3, `h3o`/`h3i` = its outgoing/incoming Bezier handle: the node whose handles are
        // on screen. Clicking the body of the object already selected keeps them visible.
        this.selNode = /^[nh]\d/.test(hit.handle) ? parseInt(hit.handle.slice(1), 10) : (sameObject ? this.selNode : null);
        const o = this.getObj(hit.type, hit.id);
        // The snapshot carries its TYPE as well as its numbers: the magnet reads a snapshot the same way
        // it reads a live obstacle (see magnetPoints), and the move maths works from the snapshot so the
        // object does not add a fresh delta at every mouse move.
        const snap = hit.type === 'circle' ? { type: 'circle', x: o.x, y: o.y, radius: o.radius }
          : hit.type === 'rect' ? { type: 'rect', x: o.x, y: o.y, width: o.width, height: o.height, angle: o.angle ?? 0 }
          : (hit.type === 'poly' || hit.type === 'road' || hit.type === 'terrain') ? { points: structuredClone(o.points ?? []) }
          : { x1: o.x1, y1: o.y1, x2: o.x2, y2: o.y2, cx: o.cx, cy: o.cy };
        this.drag = { type: hit.type, id: hit.id, handle: hit.handle, sw: { ...wp }, snap };
        this.notify();
      } else {
        this.setSelected(null);
      }
    } else if (this.mode === 'addPoly' || this.mode === 'addRoad' || this.mode === 'addTerrainArea') {
      this.addDraftPoint(wp);
      this.render();
    } else {
      const at = this.magnetFor(wp.x, wp.y, null);
      this.guides = at.guides;
      if (!this.addFirst) {
        this.addFirst = { x: at.x, y: at.y };
      } else {
        this.placeItem(this.addFirst, { x: at.x, y: at.y });
        this.addFirst = null;
        this.guides = { x: null, y: null };
      }
      this.render();
    }
  }

  onMove(e) {
    const sp = this.mousePos(e);
    const wp = this.s2w(sp.x, sp.y);
    this.mw = wp;

    this.onCoords(Math.round(wp.x), Math.round(wp.y));

    if (this.panState) {
      this.panX = this.panState.px - (sp.x - this.panState.ax) / this.zoom;
      this.panY = this.panState.py - (sp.y - this.panState.ay) / this.zoom;
      this.render();
      return;
    }

    if (this.drag) {
      const dx = wp.x - this.drag.sw.x, dy = wp.y - this.drag.sw.y;
      const o = this.getObj(this.drag.type, this.drag.id), s = this.drag.snap;
      if (this.drag.type === 'circle') {
        if (this.drag.handle === 'radius') {
          this.snapTarget = null;
          this.guides = { x: null, y: null };
          o.radius = Math.max(5, Math.hypot(wp.x - o.x, wp.y - o.y));
        } else {
          // The whole stack follows whichever of its own points the magnet catches (from the snapshot, so
          // the offset stays absolute — see magnetOffset).
          const moved = this.magnetOffset(this.magnetPoints(s), dx, dy, { type: 'circle', id: this.drag.id });
          this.guides = moved.guides;
          this.snapTarget = moved.snapped;
          o.x = s.x + moved.offX; o.y = s.y + moved.offY;
        }
      } else if (this.drag.type === 'rect') {
        if (this.drag.handle === 'resize') {
          this.snapTarget = null;
          this.guides = { x: null, y: null };
          const cos = Math.cos(-s.angle), sin = Math.sin(-s.angle);
          const ddx = wp.x - s.x, ddy = wp.y - s.y;
          const lx = ddx * cos - ddy * sin, ly = ddx * sin + ddy * cos;
          const width = Math.max(10, Math.abs(lx) * 2);
          const height = Math.max(10, Math.abs(ly) * 2);
          const ratio = spriteRatio(o);
          if (ratio) {
            // One sprite fitted to the rectangle, so its two sides are one shape: the side the drag has
            // moved furthest from where it started drives, and the other follows to keep the ratio.
            // Whichever the hand is on is the one the eye is following.
            if (Math.abs(width / s.width - 1) >= Math.abs(height / s.height - 1)) {
              o.width = width;
              o.height = Math.max(10, width / ratio);
            } else {
              o.height = height;
              o.width = Math.max(10, height * ratio);
            }
          } else {
            o.width = width;
            o.height = height;
          }
        } else if (this.drag.handle === 'rotate') {
          this.snapTarget = null;
          this.guides = { x: null, y: null };
          // Keep the angle the pointer already had on the handle when it was grabbed: without it the
          // shape snaps round to point at the cursor the moment the drag starts.
          const grabbed = Math.atan2(this.drag.sw.y - s.y, this.drag.sw.x - s.x);
          const now = Math.atan2(wp.y - s.y, wp.x - s.x);
          const turned = s.angle + (now - grabbed);
          o.angle = e.shiftKey ? Math.round(turned / (Math.PI / 12)) * (Math.PI / 12) : turned;
        } else {
          // Move it whole, letting its own corners and middle line up with anything nearby: this is how
          // two stands (or a stand and a wall) are put exactly side by side. The offset comes from the
          // snapshot, so the shape does not add a fresh delta at every mouse move.
          const moved = this.magnetOffset(this.magnetPoints(s), dx, dy, { type: 'rect', id: this.drag.id });
          this.guides = moved.guides;
          this.snapTarget = moved.snapped;
          o.x = s.x + moved.offX; o.y = s.y + moved.offY;
        }
      } else if (this.drag.type === 'poly' || this.drag.type === 'road' || this.drag.type === 'terrain') {
        const key = this.drag.handle;
        const exclude = { type: this.drag.type, id: this.drag.id };
        if (key.startsWith('n')) {
          const i = parseInt(key.slice(1), 10);
          const at = this.magnetFor(wp.x, wp.y, exclude);
          this.snapTarget = at.guides.x != null || at.guides.y != null ? { x: at.x, y: at.y } : null;
          this.guides = at.guides;
          this.moveNode(o, i, at.x, at.y);
        } else if (key.startsWith('h')) {
          // Handles mirror the opposite one (smooth node) unless Alt is held: holding Alt is how a
          // corner — or the two halves of an S — stay independent.
          const i = parseInt(key.slice(1), 10);
          this.snapTarget = null;
          this.guides = { x: null, y: null };
          this.moveHandle(o, i, key.endsWith('o') ? 'out' : 'in', wp.x, wp.y, !e.altKey);
        } else {
          // 'mid' (the shape's middle handle) or 'body' (anywhere on it): move it all — and let the
          // magnet catch any of its own nodes on another element's node.
          //
          // Both the magnet and the move work from the shape AS IT WAS when the drag started: the
          // offset magnetOffset returns is measured from there (dx/dy are too), so applying it to the
          // shape's CURRENT nodes adds a fresh delta at every mouse move and the shape runs away.
          const moved = this.magnetOffset(s.points, dx, dy, { type: this.drag.type, id: this.drag.id });
          this.guides = moved.guides;
          this.snapTarget = moved.snapped;
          o.points = s.points.map((p) => this.shiftNode(p, moved.offX, moved.offY));
        }
      } else if (this.drag.handle === 'p1' || this.drag.handle === 'p2') {
        // A straight piece's end (segment, checkpoint, start grid, pit box, wall): the magnet can land
        // it on any node of the track — another segment's end, a road area's node, a polygon's node.
        const exclude = { type: this.drag.type, id: this.drag.id };
        const at = this.magnetFor(wp.x, wp.y, exclude);
        this.snapTarget = at.guides.x != null || at.guides.y != null ? { x: at.x, y: at.y } : null;
        this.guides = at.guides;
        if (this.drag.handle === 'p1') { o.x1 = at.x; o.y1 = at.y; }
        else { o.x2 = at.x; o.y2 = at.y; }
      } else if (this.drag.handle === 'ctrl') {
        this.snapTarget = null; o.cx = wp.x; o.cy = wp.y; this.notify();
      } else {
        // Moving the whole segment: the magnet catches its ENDS on anything, not the grab point.
        const moved = this.magnetOffset(
          [{ x: s.x1, y: s.y1 }, { x: s.x2, y: s.y2 }],
          dx, dy,
          { type: 'seg', id: this.drag.id },
        );
        this.guides = moved.guides;
        this.snapTarget = moved.snapped;
        o.x1 = s.x1 + moved.offX; o.y1 = s.y1 + moved.offY;
        o.x2 = s.x2 + moved.offX; o.y2 = s.y2 + moved.offY;
        if (s.cx != null) { o.cx = s.cx + moved.offX; o.cy = s.cy + moved.offY; }
      }
      this.render();
      return;
    }

    if (this.mode === 'select') {
      const hit = this.hitTest(wp.x, wp.y);
      const nh = hit ? { type: hit.type, id: hit.id, handle: hit.handle } : null;
      const changed = nh?.type !== this.hovered?.type || nh?.id !== this.hovered?.id || nh?.handle !== this.hovered?.handle;
      if (changed) {
        this.hovered = nh;
        this.onCursor(!hit ? 'default'
          : (hit.handle === 'mid' || hit.handle === 'center') ? 'move'
          : (hit.handle.startsWith('n') || hit.handle === 'p1' || hit.handle === 'p2' || hit.handle === 'radius' || hit.handle === 'resize' || hit.handle === 'rotate') ? 'crosshair'
          : 'pointer');
        this.render();
      }
    } else {
      const drafting = this.mode === 'addPoly' || this.mode === 'addRoad' || this.mode === 'addTerrainArea';
      if (drafting) {
        const at = this.draftMagnet(wp);
        this.snapTarget = at.guides.x != null || at.guides.y != null ? { x: at.x, y: at.y } : null;
        this.guides = at.guides;
      } else {
        this.snapTarget = null;
        this.guides = { x: null, y: null };
      }
      // Only before the first click of a session (`draft === null`): that's when clicking an end
      // node continues an existing polygon instead of starting a new one.
      this.setHudOverride(this.extendHint(wp));
      this.render();
    }
  }

  onUp(e) {
    this.invalidateRoadSamples();
    if (e.button === 1) {
      this.panState = null;
      this.onCursor(this.mode === 'select' ? 'default' : 'crosshair');
    }
    if (e.button === 0) {
      this.drag = null;
      this.snapTarget = null;
      this.guides = { x: null, y: null };
      this.notify();
    }
  }

  // Double-click: while drawing an area/polygon it finishes the shape; in select mode it removes the
  // node under the cursor, or splits the edge under it — that is how a curve gets the extra nodes
  // you need to shape it, and the split keeps the curve exactly where it was.
  onDblClick(e) {
    if (this.mode === 'addPoly' || this.mode === 'addRoad' || this.mode === 'addTerrainArea') {
      e.preventDefault();
      this.finishDraft();
      return;
    }
    if (this.mode !== 'select') return;
    const sp = this.mousePos(e);
    const wp = this.s2w(sp.x, sp.y);
    const hit = this.hitTest(wp.x, wp.y);
    if (!hit || (hit.type !== 'poly' && hit.type !== 'road' && hit.type !== 'terrain')) return;
    if (hit.handle.startsWith('n')) {
      e.preventDefault();
      this.removeNode(hit.type, hit.id, parseInt(hit.handle.slice(1), 10));
    } else if (hit.handle === 'body') {
      e.preventDefault();
      this.insertNodeAt(hit.type, hit.id, wp.x, wp.y);
    }
  }

  onWheel(e) {
    e.preventDefault();
    const sp = this.mousePos(e), wp = this.s2w(sp.x, sp.y);
    this.zoom = Math.max(0.03, Math.min(10, this.zoom * (e.deltaY > 0 ? 0.85 : 1.18)));
    this.panX = wp.x - sp.x / this.zoom;
    this.panY = wp.y - sp.y / this.zoom;
    this.render();
  }

  onKeydown(e) {
    if (this.busy) return;
    if (document.activeElement?.matches('input, textarea, select, [contenteditable="true"]')) return;
    const key = e.key.toLowerCase();
    if ((e.ctrlKey || e.metaKey) && !e.altKey && (key === 'z' || key === 'y')) {
      e.preventDefault();
      if (key === 'y' || e.shiftKey) this.redo();
      else this.undo();
      return;
    }
    if (e.key === 'Escape') this.cancel();
    if (e.key === 'Enter' && (this.mode === 'addPoly' || this.mode === 'addRoad' || this.mode === 'addTerrainArea')) this.finishDraft();
    if (e.key === 'Delete' || e.key === 'Backspace') this.deleteSelected();
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'c') { e.preventDefault(); this.copySelected(); }
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'v') { e.preventDefault(); this.pasteClipboard(); }
  }

  // ── Actions ──────────────────────────────────────────────────
  getObj(type, id) {
    if (type === 'sg') return this.sg;
    if (type === 'pit') return this.pit;
    if (type === 'road') return this.roadAreas.find(a => a.id === id);
    if (type === 'terrain') return this.terrainAreas.find(a => a.id === id);
    if (type === 'wall' || type === 'circle' || type === 'rect' || type === 'poly') return this.obstacles.find(o => o.id === id);
    return type === 'seg' ? this.segs.find(s => s.id === id) : this.cps.find(c => c.id === id);
  }

  /** Place a palette sprite at the pointer, using catalogue sizes in world units. */
  placeSpriteObstacle(material, variant, clientX, clientY) {
    if (!['houses', 'signals'].includes(material) || !Number.isInteger(variant)
      || !Number.isFinite(clientX) || !Number.isFinite(clientY) || !this.canvas) return null;
    const sprite = MATERIALS[material].variants[variant];
    if (!sprite) return null;
    const screen = this.mousePos({ clientX, clientY });
    const point = this.s2w(screen.x, screen.y);
    this.beginHistoryEdit();
    try {
      // Clear any unfinished creation gesture while retaining the active tool.
      this.setMode(this.mode);
      const obstacle = { id: this.nextObs++, type: 'rect', x: point.x, y: point.y,
        width: sprite.length, height: sprite.depth, angle: 0, material, variant,
        restitution: this.defObsRestitution, friction: this.defObsFriction };
      this.obstacles.push(obstacle);
      this.setSelected('rect', obstacle.id);
      return obstacle;
    } finally { this.endHistoryEdit(); }
  }

  setSelected(type, id) {
    // A selected node belongs to one object; never carry its handles to a new one.
    if (!type || this.selected?.type !== type || this.selected?.id !== id) this.selNode = null;
    this.selected = type ? { type, id } : null;
    this.notify();
    this.render();
  }

  cancel() {
    if (this.mode !== 'select') { this.addFirst = null; this.setMode('select'); }
    else this.setSelected(null);
  }

  deleteSelected() {
    if (!this.selected) return;
    if (this.selected.type === 'seg') this.segs = this.segs.filter(s => s.id !== this.selected.id);
    else if (this.selected.type === 'road') this.roadAreas = this.roadAreas.filter(a => a.id !== this.selected.id);
    else if (this.selected.type === 'terrain') this.terrainAreas = this.terrainAreas.filter(a => a.id !== this.selected.id);
    else if (this.selected.type === 'cp') this.cps = this.cps.filter(c => c.id !== this.selected.id);
    else if (this.selected.type === 'sg') this.sg = null;
    else if (this.selected.type === 'pit') this.pit = null;
    else if (['wall', 'circle', 'rect', 'poly'].includes(this.selected.type)) this.obstacles = this.obstacles.filter(o => o.id !== this.selected.id);
    this.setSelected(null);
    this.render();
  }

  // Copies the selected object's shape/props (not its id) so Paste can spawn an offset duplicate.
  copySelected() {
    if (!this.selected) return;
    const { type, id } = this.selected;
    if (!['seg', 'road', 'terrain', 'cp', 'wall', 'circle', 'rect', 'poly'].includes(type)) return;
    const obj = this.getObj(type, id);
    if (!obj) return;
    const { id: _id, type: _type, ...rest } = obj;
    this.clipboard = { type, data: structuredClone(rest) };
    this.notify();
  }

  // Pastes an offset duplicate (never exactly on top of the source) so it's immediately visible/selectable.
  pasteClipboard() {
    if (!this.clipboard) return;
    const PASTE_OFFSET = 40;
    const { type, data } = this.clipboard;
    const copy = structuredClone(data);
    if (type === 'poly' || type === 'road' || type === 'terrain') {
      copy.points = copy.points.map(p => this.shiftNode(p, PASTE_OFFSET, PASTE_OFFSET));
    } else if (type === 'circle' || type === 'rect') {
      copy.x += PASTE_OFFSET; copy.y += PASTE_OFFSET;
    } else {
      copy.x1 += PASTE_OFFSET; copy.y1 += PASTE_OFFSET;
      copy.x2 += PASTE_OFFSET; copy.y2 += PASTE_OFFSET;
      if (copy.cx != null) copy.cx += PASTE_OFFSET;
      if (copy.cy != null) copy.cy += PASTE_OFFSET;
    }

    let newId;
    if (type === 'seg') {
      newId = this.nextSeg++;
      this.segs.push({ id: newId, ...copy });
    } else if (type === 'road') {
      newId = this.nextArea++;
      this.roadAreas.push({ id: newId, ...copy });
    } else if (type === 'terrain') {
      newId = this.nextTerrain++;
      this.terrainAreas.push({ id: newId, ...copy });
    } else if (type === 'cp') {
      newId = this.nextCp++;
      this.cps.push({ id: newId, ...copy });
    } else {
      newId = this.nextObs++;
      this.obstacles.push({ id: newId, type, ...copy });
    }

    this.setMode(this.mode);
    this.setSelected(type, newId);
  }

  // Mirrors the selected object horizontally around its own center (in place, no offset).
  flipSelectedHorizontal() {
    if (!this.selected) return;
    const { type, id } = this.selected;
    const obj = this.getObj(type, id);
    if (!obj) return;
    if (type === 'rect') {
      obj.angle = -(obj.angle || 0);
    } else if (type === 'poly' || type === 'road' || type === 'terrain') {
      // Mirror every node (handles included) around the shape's own horizontal extent, in place.
      const pts = obj.points ?? [];
      if (!pts.length) return;
      const minX = Math.min(...pts.map(p => p.x)), maxX = Math.max(...pts.map(p => p.x));
      const mirror = (p) => ({ x: minX + maxX - p.x, y: p.y });
      obj.points = pts.map(p => {
        const out = mirror(p);
        if (p.hIn) out.hIn = mirror(p.hIn);
        if (p.hOut) out.hOut = mirror(p.hOut);
        return out;
      });
    } else if (type !== 'circle') {
      const x1 = obj.x1;
      obj.x1 = obj.x2;
      obj.x2 = x1;
      if (obj.cx != null) obj.cx = obj.x1 + obj.x2 - obj.cx;
    }
    this.notify();
    this.render();
  }

  placeItem(p1, p2) {
    let created = null;
    if (this.mode === 'addSeg') {
      this.segs.push({ id: this.nextSeg++, x1: p1.x, y1: p1.y, x2: p2.x, y2: p2.y, width: this.defWidth, lane: this.defLane });
      created = { type: 'seg', id: this.nextSeg - 1 };
    } else if (this.mode === 'addCp') {
      this.cps.push({ id: this.nextCp++, x1: p1.x, y1: p1.y, x2: p2.x, y2: p2.y });
      created = { type: 'cp', id: this.nextCp - 1 };
    } else if (this.mode === 'addSg') {
      // The facing is the new line's own normal (+90° side), what the old `dir: 1` default meant.
      const facing = Math.atan2(p2.y - p1.y, p2.x - p1.x) + Math.PI / 2;
      this.sg = { x1: p1.x, y1: p1.y, x2: p2.x, y2: p2.y, angle: facing, staggerPx: 20, flip: false };
      created = { type: 'sg', id: 'sg' };
    } else if (this.mode === 'addPit') {
      this.pit = { x1: p1.x, y1: p1.y, x2: p2.x, y2: p2.y,
                   crewSide: 'auto', crewOffset: DEFAULT_CREW_OFFSET, direction: 'auto' };
      created = { type: 'pit', id: 'pit' };
    } else if (this.mode === 'addWall') {
      this.obstacles.push({ id: this.nextObs++, type: 'wall', x1: p1.x, y1: p1.y, x2: p2.x, y2: p2.y,
        thickness: this.defObsThickness, restitution: this.defObsRestitution, friction: this.defObsFriction, material: knownMaterial(this.defObsMaterial, 'wall') });
      created = { type: 'wall', id: this.nextObs - 1 };
    } else if (this.mode === 'addCircle') {
      const radius = Math.max(5, Math.hypot(p2.x - p1.x, p2.y - p1.y));
      this.obstacles.push({ id: this.nextObs++, type: 'circle', x: p1.x, y: p1.y, radius,
        restitution: this.defObsRestitution, friction: this.defObsFriction, material: knownMaterial(this.defObsMaterial, 'circle') });
      created = { type: 'circle', id: this.nextObs - 1 };
    } else if (this.mode === 'addRect') {
      const x = (p1.x + p2.x) / 2, y = (p1.y + p2.y) / 2;
      const rect = { id: this.nextObs++, type: 'rect', x, y, angle: 0,
        width: Math.max(10, Math.abs(p2.x - p1.x)), height: Math.max(10, Math.abs(p2.y - p1.y)),
        restitution: this.defObsRestitution, friction: this.defObsFriction, material: knownMaterial(this.defObsMaterial, 'rect') };
      this.obstacles.push(rect);
      // A sprite material is drawn from a picture: the rectangle is fitted to its proportions.
      this.fitSprite(rect);
      created = { type: 'rect', id: rect.id };
    } else if (this.mode === 'addTerrainRect') {
      // A terrain figure is a closed polygon whatever its shape: a rectangle is the four corners.
      const x1 = Math.min(p1.x, p2.x), y1 = Math.min(p1.y, p2.y);
      const x2 = Math.max(p1.x, p2.x), y2 = Math.max(p1.y, p2.y);
      this.terrainAreas.push({ id: this.nextTerrain++, terrain: this.defTerrain, closed: true,
        points: [{ x: x1, y: y1 }, { x: x2, y: y1 }, { x: x2, y: y2 }, { x: x1, y: y2 }] });
      created = { type: 'terrain', id: this.nextTerrain - 1 };
    } else if (this.mode === 'addTerrainCircle') {
      // A circle is the same closed polygon, approximated with a fixed ring of nodes (no handles:
      // `curveNodes` can always turn it into a smooth one later).
      const radius = Math.max(5, Math.hypot(p2.x - p1.x, p2.y - p1.y));
      const points = [];
      for (let i = 0; i < 32; i++) {
        const a = (i / 32) * Math.PI * 2;
        points.push({ x: p1.x + Math.cos(a) * radius, y: p1.y + Math.sin(a) * radius });
      }
      this.terrainAreas.push({ id: this.nextTerrain++, terrain: this.defTerrain, closed: true, points });
      created = { type: 'terrain', id: this.nextTerrain - 1 };
    }
    if (created) {
      // Clear the completed gesture and select its result, keeping the active tool.
      this.setMode(this.mode);
      this.setSelected(created.type, created.id);
    }
  }

  // An end node of an existing polygon sitting under the cursor, if any. Clicking it while drawing
  // continues that polygon instead of starting a new one (the pen-tool behaviour of vector editors).
  // Only the two ENDPOINTS are continuable: a `poly` is a plain node list, so there is nowhere to
  // branch off from a node in the middle.
  polyEndNodeAt(wx, wy) {
    for (let i = this.obstacles.length - 1; i >= 0; i--) {
      const o = this.obstacles[i];
      const pts = o.points ?? [];
      if (o.type !== 'poly' || pts.length < 2) continue;
      if (this.nearHandle(wx, wy, pts[0].x, pts[0].y)) return { id: o.id, atStart: true };
      if (this.nearHandle(wx, wy, pts[pts.length - 1].x, pts[pts.length - 1].y)) return { id: o.id, atStart: false };
    }
    return null;
  }

  // The existing node the current draft is being appended to, or null when drawing a new polygon.
  extendAnchor() {
    if (!this.extend) return null;
    const pts = this.getObj('poly', this.extend.id)?.points ?? [];
    return (this.extend.atStart ? pts[0] : pts[pts.length - 1]) ?? null;
  }

  // What the next node would snap to, if anything is in range: the draft's own first node (clicking
  // it back closes a polygon) or the magnet (nodes of the other elements, then their alignment axes).
  draftMagnet(wp) {
    const first = this.draft?.[0];
    if (first && this.nearHandle(wp.x, wp.y, first.x, first.y)) {
      return { x: first.x, y: first.y, guides: { x: first.x, y: first.y } };
    }
    return this.magnetFor(wp.x, wp.y, null);
  }

  addDraftPoint(wp) {
    if (!this.draft) {
      // First click of a drawing session: continuing an existing polygon wins over snapping a new
      // one onto the same node (onMove shows a HUD hint while such a node is under the cursor).
      this.draftKind = this.mode === 'addRoad' ? 'area' : this.mode === 'addTerrainArea' ? 'terrain' : 'poly';
      const end = this.draftKind === 'poly' ? this.polyEndNodeAt(wp.x, wp.y) : null;
      this.draft = [];
      if (end) { this.extend = end; this.render(); return; }
    }
    const at = this.draftMagnet(wp);
    this.draft.push({ x: at.x, y: at.y });
  }

  /**
   * Completes and selects the draft without leaving its drawing tool.
   * Fewer than 3 nodes is discarded for an area (it needs a surface),
   * fewer than 2 for a polyline.
   */
  finishDraft() {
    const kind = this.draftKind;
    const extend = this.extend;
    const nodes = this.cleanDraft(extend ? this.extendAnchor() : null);
    this.draft = null;
    this.extend = null;

    if (extend) {
      const obj = this.getObj(extend.type, extend.id);
      this.setMode(this.mode);
      if (obj?.points && nodes.length) {
        // Nodes go on the side the endpoint was clicked from, so a draft anchored at the FIRST node
        // is inserted reversed.
        obj.points = extend.atStart ? [...nodes.reverse(), ...obj.points] : [...obj.points, ...nodes];
        this.setSelected(extend.type, obj.id);
      }
      return;
    }

    if (kind === 'area') {
      // An area is a perimeter: it closes back on its first node, and a last node that landed there
      // (the user clicked it) is dropped instead of becoming a duplicate.
      const first = nodes[0], last = nodes[nodes.length - 1];
      const points = (nodes.length > 3 && Math.hypot(last.x - first.x, last.y - first.y) <= 4 / this.zoom)
        ? nodes.slice(0, -1) : nodes;
      if (points.length < 3) { this.setMode(this.mode); return; }
      const id = this.nextArea++;
      this.roadAreas.push({ id, closed: true, points });
      this.setMode(this.mode);
      this.setSelected('road', id);
      return;
    }

    if (kind === 'terrain') {
      // Same perimeter rule as a road area, but the figure is ground, not asphalt, and carries a
      // terrain name instead of a paint style.
      const first = nodes[0], last = nodes[nodes.length - 1];
      const points = (nodes.length > 3 && Math.hypot(last.x - first.x, last.y - first.y) <= 4 / this.zoom)
        ? nodes.slice(0, -1) : nodes;
      if (points.length < 3) { this.setMode(this.mode); return; }
      const id = this.nextTerrain++;
      this.terrainAreas.push({ id, terrain: this.defTerrain, closed: true, points });
      this.setMode(this.mode);
      this.setSelected('terrain', id);
      return;
    }

    if (nodes.length < 2) { this.setMode(this.mode); return; }
    const id = this.nextObs++;
    this.obstacles.push({ id, type: 'poly', points: nodes,
      thickness: this.defPolyThickness,
      restitution: this.defObsRestitution, friction: this.defObsFriction, material: knownMaterial(this.defObsMaterial, 'poly') });
    this.setMode(this.mode);
    this.setSelected('poly', id);
  }

  // Drops nodes closer than ~4 screen px to the previous one: finishing with a double-click adds
  // the same point twice, and that trailing duplicate shouldn't become a real node. When extending,
  // `anchor` (the node the draft grows from) counts as the point before the first one.
  cleanDraft(anchor = null) {
    const minDist = 4 / this.zoom;
    const points = [];
    let last = anchor;
    for (const p of this.draft ?? []) {
      if (last && Math.hypot(p.x - last.x, p.y - last.y) < minDist) continue;
      points.push({ x: p.x, y: p.y });
      last = p;
    }
    return points;
  }

  // HUD nudge shown while hovering an extendable end node in draw mode — the gesture would
  // otherwise be invisible, since nothing is drawn for it.
  extendHint(wp) {
    if (this.mode !== 'addPoly' || this.draft || this.extend) return null;
    return this.polyEndNodeAt(wp.x, wp.y) ? 'Click to continue this polygon from here  (Esc to cancel)' : null;
  }

  setHudOverride(text) {
    if (this.hudOverride === text) return;
    this.hudOverride = text;
    this.notify();
  }

  /** Splits the edge of an area/polygon under the cursor, adding a node where it was clicked. */
  insertNodeAt(type, id, wx, wy) {
    const obj = this.getObj(type, id);
    if (!obj?.points) return;
    const hit = this.closestEdge(obj.points, !!obj.closed, wx, wy);
    if (!hit) return;
    const index = this.insertNode(obj.points, hit.edge, !!obj.closed, hit.t);
    if (index < 0) return;
    this.selNode = index;
    this.notify();
    this.render();
  }

  /** Removes one node. A shape left with fewer nodes than it needs to exist is deleted entirely
   * rather than left as a stray point. */
  removeNode(type, id, index) {
    const obj = this.getObj(type, id);
    if (!obj?.points) return;
    obj.points.splice(index, 1);
    const minimum = (type === 'road' || type === 'terrain') ? 3 : 2;
    if (obj.points.length < minimum) { this.deleteSelected(); return; }
    if (this.selNode != null && this.selNode >= obj.points.length) this.selNode = null;
    this.notify();
    this.render();
  }

  /** Straightens the selected node: its handles go away and both its edges become straight lines. */
  straightenNode(type, id) {
    const obj = this.getObj(type, id);
    const node = this.selNode != null ? obj?.points?.[this.selNode] : null;
    if (!node) return;
    delete node.hIn;
    delete node.hOut;
    this.notify();
    this.render();
  }

  /**
   * The handles that make the outline pass through a node as part of one smooth curve: the cubic edge
   * on either side is laid along the circle through the node and the two beside it, with the handle
   * length of a circular arc — 4/3 · R · tan(θ/4), θ being the arc that edge spans. That constant is
   * what puts the cubic ON the circle instead of merely touching it at the ends, which is the
   * difference between "a curve" and the staircase of straight chords the nodes were clicked as.
   *
   * Chordal by construction: each side's handle length follows that side's own arc, so a node between
   * a long edge and a short one comes out right instead of overshooting on the short side.
   *
   * A node in line with its neighbours has no circle: its handles are dropped, and a straight run
   * stays exactly straight. `null` (rather than a pair) means the node has no neighbour on one side —
   * an open end, which has no direction to speak of.
   */
  curveHandles(nodes, closed) {
    const count = nodes.length;
    const collinear = 0.05; // world units of sagitta below which three nodes are a straight line
    return nodes.map((node, i) => {
      const prev = closed ? nodes[(i - 1 + count) % count] : nodes[i - 1];
      const next = closed ? nodes[(i + 1) % count] : nodes[i + 1];
      if (!prev || !next || !node) return null;
      const center = circleThrough(prev, node, next, collinear);
      if (!center) return { out: null, in: null };
      const radius = Math.hypot(node.x - center.x, node.y - center.y);
      if (!(radius > 0)) return { out: null, in: null };
      const ux = (node.x - center.x) / radius;
      const uy = (node.y - center.y) / radius;
      // Tangent at the node, turned to point along the way the outline travels (toward `next`).
      const sign = (next.x - node.x) * -uy + (next.y - node.y) * ux >= 0 ? 1 : -1;
      const tx = -uy * sign;
      const ty = ux * sign;
      const reach = (p) => {
        const vx = (p.x - center.x) / radius;
        const vy = (p.y - center.y) / radius;
        const arc = Math.acos(Math.max(-1, Math.min(1, ux * vx + uy * vy)));
        return (4 / 3) * radius * Math.tan(arc / 4);
      };
      return {
        out: { x: tx * reach(next), y: ty * reach(next) },
        in: { x: -tx * reach(prev), y: -ty * reach(prev) },
      };
    });
  }

  /**
   * The two flags the panels use for the selected node of a node list: `nodeHasHandles` = there is a
   * curve to straighten, `nodeIsSmoothed` = the curve through it is already the one `curveNodes` lays,
   * so that button has nothing left to do and is greyed out.
   *
   * Handles dragged by hand are NOT "smoothed": they are a curve, just not this one, and laying the
   * circle through the node on them is still an improvement worth offering.
   */
  nodeFlags(obj) {
    const nodes = obj?.points ?? [];
    const index = this.selNode;
    const node = index != null ? nodes[index] : null;
    if (!node) return { nodeHasHandles: false, nodeIsSmoothed: false };
    const hasHandles = !!(node.hIn || node.hOut);
    if (!node.hIn || !node.hOut) return { nodeHasHandles: hasHandles, nodeIsSmoothed: false };
    const target = this.curveHandles(nodes, !!obj.closed)[index];
    if (!target?.in || !target?.out) return { nodeHasHandles: true, nodeIsSmoothed: false };
    const near = (a, b) => Math.hypot(a.x - b.x, a.y - b.y) < 0.5;
    return {
      nodeHasHandles: true,
      nodeIsSmoothed: near(node.hIn, { x: node.x + target.in.x, y: node.y + target.in.y })
        && near(node.hOut, { x: node.x + target.out.x, y: node.y + target.out.y }),
    };
  }

  /**
   * Curves the shape through its nodes: the corners the user clicked become the arc they were drawing.
   * Placing a node every few units along a bend is how a curve is drawn in this editor, and this turns
   * that staircase back into the bend — three or four nodes on it are enough.
   *
   * `whole` smooths the whole outline (every node at once); otherwise only the selected node and the
   * two edges meeting there are touched, which also sets the FACING handle of the two neighbours: with
   * one end of an edge curved and the other straight, the corner being fixed only moves along it.
   */
  curveNodes(type, id, whole = false) {
    const obj = this.getObj(type, id);
    const nodes = obj?.points ?? [];
    const count = nodes.length;
    if (count < 3) return;
    const closed = !!obj.closed;
    const handles = this.curveHandles(nodes, closed);
    const wrap = (i) => (closed ? (i + count) % count : i);
    const setHandles = (i, side) => {
      const index = wrap(i);
      if (index < 0 || index >= count) return;
      const node = nodes[index], handle = handles[index];
      if (!node || !handle) return;
      if (side !== 'in') {
        if (handle.out) node.hOut = { x: node.x + handle.out.x, y: node.y + handle.out.y };
        else delete node.hOut;
      }
      if (side !== 'out') {
        if (handle.in) node.hIn = { x: node.x + handle.in.x, y: node.y + handle.in.y };
        else delete node.hIn;
      }
    };

    const selected = whole ? null : this.selNode;
    if (selected != null) {
      setHandles(selected, 'both');
      setHandles(selected - 1, 'out');
      setHandles(selected + 1, 'in');
    } else if (whole) {
      for (let i = 0; i < count; i++) setHandles(i, 'both');
    } else return;
    this.notify();
    this.render();
  }

  setMode(m) {
    this.mode = m;
    this.addFirst = null;
    this.draft = null;
    this.draftKind = 'poly';
    this.extend = null;
    this.hudOverride = null;
    this.guides = { x: null, y: null };
    this.onCursor(m === 'select' ? 'default' : 'crosshair');
    this.notify();
    this.render();
  }

  toggleMode(m) { this.setMode(this.mode === m ? 'select' : m); }

  setDefaultWidth(w) { this.defWidth = Math.max(20, parseInt(w) || 180); }
  setDefaultLane(lane) { this.defLane = lane; }
  setMagneticSnap(on) { this.magneticSnap = on; this.notify(); }
  setHideObjects(on) { this.hideObjects = !!on; this.notify(); this.render(); }
  setDefaultObsThickness(t) { this.defObsThickness = Math.max(2, parseInt(t) || this.defObsThickness); }
  setDefaultObsRestitution(r) { this.defObsRestitution = Math.max(0, Math.min(1, parseFloat(r) || 0)); }
  setDefaultObsFriction(f) { this.defObsFriction = Math.max(0, parseFloat(f) ?? this.defObsFriction); }
  setDefaultObsMaterial(m) { this.defObsMaterial = Object.hasOwn(MATERIALS, m) ? m : DEFAULT_MATERIAL; }
  setDefaultPolyThickness(t) { this.defPolyThickness = Math.max(2, parseInt(t) || this.defPolyThickness); }

  /** The default terrain a NEW figure is given (grass would be a no-op, so the default is dirt). */
  setDefaultTerrain(t) { this.defTerrain = TERRAIN_STYLES.includes(t) ? t : this.defTerrain; }

  /** The terrain of one figure: the name the game's simulation reads (see physicsConfig.TERRAINS). */
  setTerrain(id, value) {
    const area = this.getObj('terrain', id);
    if (!area) return;
    area.terrain = TERRAIN_STYLES.includes(value) ? value : terrainOf(area);
    this.notify();
    this.render();
  }

  setSegWidth(id, w) {
    const s = this.getObj('seg', id);
    if (s) { s.width = Math.max(20, parseInt(w) || s.width); this.notify(); this.render(); }
  }
  setSegLane(id, lane) {
    const s = this.getObj('seg', id);
    if (s) { s.lane = lane; this.notify(); this.render(); }
  }

  /**
   * The look one piece of asphalt is painted with: the painter picks its textures from this
   * name (see ./trackPainter.js), so a track can mix styles piece by piece later on (kerbs, other
   * surfaces). Storing the DEFAULT as no field at all keeps the exported file as short as it has
   * always been — and an old file has no field either, so it means exactly the same thing.
   */
  setSurface(type, id, value) {
    const obj = this.getObj(type, id);
    if (!obj) return;
    if (SURFACE_STYLES.includes(value) && value !== DEFAULT_SURFACE) obj.surface = value;
    else delete obj.surface;
    this.notify();
    this.render();
  }

  /** Marks a road area as the pit lane: its line is painted yellow and the game treats it as the
   * pit lane (speed/pit logic), which is what an area-drawn pit lane needs to behave like a branch. */
  setAreaPit(id, on) {
    const area = this.getObj('road', id);
    if (!area) return;
    area.pit = !!on;
    this.notify();
    this.render();
  }

  setObsRestitution(type, id, r) {
    const o = this.getObj(type, id);
    if (o) { o.restitution = Math.max(0, Math.min(1, parseFloat(r) ?? o.restitution)); this.notify(); this.render(); }
  }
  setObsFriction(type, id, f) {
    const o = this.getObj(type, id);
    if (o) { o.friction = Math.max(0, parseFloat(f) ?? o.friction); this.notify(); this.render(); }
  }
  setObsMaterial(type, id, m) {
    const o = this.getObj(type, id);
    if (!o) return;
    o.material = knownMaterial(m, type);
    // Becoming (or ceasing to be) a sprite material changes what shape the rectangle is allowed to be:
    // fitting it here means switching the material is enough, without nudging the size by hand.
    if (type === 'rect') {
      if (materialVariant(o.material, o.variant ?? 0)) {
        if (!Number.isInteger(o.variant)) o.variant = 0;
        this.fitSprite(o);
      } else delete o.variant;
    }
    this.notify();
    this.render();
  }

  /** Which sprite a shape uses (the material's `variants` list). Only rects can be sprite materials. */
  setObsVariant(type, id, variant) {
    const o = this.getObj(type, id);
    if (!o || spriteRatio(o) === null) return;
    const index = Math.max(0, parseInt(variant) || 0);
    o.variant = index;
    this.fitSprite(o);
    this.notify();
    this.render();
  }

  /**
   * Fits a sprite material to the proportions its picture needs (see spriteRatio), holding on to the side
   * the author aimed at: the longer of the two drives and the other follows. Does nothing to anything else.
   */
  fitSprite(obstacle) {
    const ratio = spriteRatio(obstacle);
    if (!ratio) return;
    const width = Math.max(10, obstacle.width);
    const height = Math.max(10, obstacle.height);
    if (width >= height) {
      obstacle.width = width;
      obstacle.height = Math.max(10, Math.round(width / ratio));
    } else {
      obstacle.height = height;
      obstacle.width = Math.max(10, Math.round(height * ratio));
    }
  }
  setWallThickness(id, t) {
    const o = this.getObj('wall', id);
    if (o) { o.thickness = Math.max(2, parseInt(t) || o.thickness); this.notify(); this.render(); }
  }
  setPolyThickness(id, t) {
    const o = this.getObj('poly', id);
    if (o) { o.thickness = Math.max(2, parseInt(t) || o.thickness); this.notify(); this.render(); }
  }
  setCircleRadius(id, r) {
    const o = this.getObj('circle', id);
    if (o) { o.radius = Math.max(5, parseInt(r) || o.radius); this.notify(); this.render(); }
  }
  setRectWidth(id, w) {
    const o = this.getObj('rect', id);
    if (!o) return;
    o.width = Math.max(10, parseInt(w) || o.width);
    const ratio = spriteRatio(o);
    if (ratio) o.height = Math.max(10, Math.round(o.width / ratio));
    this.notify();
    this.render();
  }
  setRectHeight(id, h) {
    const o = this.getObj('rect', id);
    if (!o) return;
    o.height = Math.max(10, parseInt(h) || o.height);
    const ratio = spriteRatio(o);
    if (ratio) o.width = Math.max(10, Math.round(o.height * ratio));
    this.notify();
    this.render();
  }
  setRectAngleDeg(id, deg) {
    const o = this.getObj('rect', id);
    if (o) { o.angle = (parseFloat(deg) || 0) * Math.PI / 180; this.notify(); this.render(); }
  }

  curveSelectedSeg() {
    if (this.selected?.type !== 'seg') return;
    const s = this.getObj('seg', this.selected.id);
    if (!s || s.cx != null) return;
    const dx = s.x2 - s.x1, dy = s.y2 - s.y1, len = Math.hypot(dx, dy);
    const nx = len > 0 ? -dy / len : 0, ny = len > 0 ? dx / len : 1;
    s.cx = (s.x1 + s.x2) / 2 + nx * s.width;
    s.cy = (s.y1 + s.y2) / 2 + ny * s.width;
    this.notify(); this.render();
  }

  straightenSelectedSeg() {
    if (this.selected?.type !== 'seg') return;
    const s = this.getObj('seg', this.selected.id);
    if (s) { delete s.cx; delete s.cy; this.notify(); this.render(); }
  }

  flipStartDir() {
    if (this.sg) { this.sg.angle = normAngle(this.sg.angle + Math.PI); this.notify(); this.render(); }
  }

  setSgStaggerPx(px) {
    if (this.sg) { this.sg.staggerPx = Math.max(0, parseInt(px) || 0); this.notify(); this.render(); }
  }
  /**
   * Which edge of the pit box the pit crew walks out of (see the game's rendering/pitCrew).
   * 'auto' means "not specified": the game then picks one based on how the car is parked.
   */
  setPitCrewSide(side) {
    if (!this.pit) return;
    this.pit.crewSide = PIT_CREW_SIDES.includes(side) ? side : 'auto';
    this.notify();
    this.render();
  }

  /**
   * How far outside that edge the crew appears, in px (the red dashed line in the canvas).
   */
  setPitCrewOffset(px) {
    if (!this.pit) return;
    this.pit.crewOffset = clampCrewOffset(px);
    this.notify();
    this.render();
  }

  /**
   * Which way the cars parked in the box face: one of PIT_DIRECTIONS, or an angle in radians for a
   * box the game's own rule cannot work out (see pitFacing).
   */
  setPitDirection(value) {
    if (!this.pit) return;
    this.pit.direction = knownPitDirection(value);
    this.notify();
    this.render();
  }

  /** The pit lane's own samples (see roadSamples), or none when the track has no pit lane. */
  pitLaneSamples() {
    return this.roadSamples().filter((s) => s.band && (s.piece.pit === true || s.piece.lane === 'pit'));
  }

  /**
   * Direction the lap is driven past `point`: the direction of the stretch between the two
   * checkpoints closest to it. The checkpoints are crossed IN ORDER, so they — and nothing else in
   * the file — say which way a track is driven; the geometry is stored in the sense it was drawn in,
   * which can be the opposite (see pitFacing).
   */
  lapAngle(point) {
    const centres = this.cps.map((c) => ({ x: (c.x1 + c.x2) / 2, y: (c.y1 + c.y2) / 2 }));
    if (centres.length < 2) return null;
    const near = nearestRunAngle([{ points: centres, closed: true }], point);
    return near ? near.angle : null;
  }

  /**
   * Which way the cars parked in the box face — the game's own rule, mirrored here because the
   * `direction` option is only usable if you can SEE the facing it produces (the white arrow in the
   * canvas). NOTHING IS SHARED with the game: a change to boxFacing/trackForwardAt/alignToLap in
   * gpo2/src/server/game/trackUtils.js has to be mirrored here, or the editor paints one facing and
   * the game parks the cars on the other.
   *
   * The slope is the pit lane's own at the box — the road's, if no lane comes close, then the start
   * direction — and the sense comes from the box's `direction` when it has one, otherwise from the
   * lap (see lapAngle).
   */
  pitFacing() {
    const box = this.pit;
    if (!box) return this.startPose().startAngle;
    const centre = { x: (box.x1 + box.x2) / 2, y: (box.y1 + box.y2) / 2 };
    const lane = nearestRunAngle(this.pitLaneSamples(), centre, PIT_LANE_REACH);
    const road = lane ?? nearestRunAngle(this.roadSamples().filter((s) => s.band && !(s.piece.pit === true || s.piece.lane === 'pit')), centre);
    const slope = road ? road.angle : this.startPose().startAngle;

    const dir = box.direction ?? 'auto';
    if (typeof dir === 'number') return dir;
    if (dir === 'forward') return slope;
    if (dir === 'backward') return normAngle(slope + Math.PI);

    const lap = this.lapAngle(centre);
    if (lap === null || Math.cos(slope - lap) >= 0) return slope;
    return normAngle(slope + Math.PI);
  }

  /**
   * Crew side actually in use: the pit box's own choice, or the game's 'auto' rule applied to the
   * facing the cars are parked with (see pitFacing) — a car broadside to the crew's edge puts the
   * mechanics on the right, one nose-in to it along the bottom (see rendering/pitCrew/crewLayout.js).
   */
  pitCrewSide() {
    const side = this.pit?.crewSide;
    if (side && side !== 'auto') return side;
    const a = this.pitFacing();
    return Math.abs(Math.cos(a)) < Math.abs(Math.sin(a)) ? 'right' : 'bottom';
  }
  setSgFlip(flip) {
    if (this.sg) { this.sg.flip = !!flip; this.notify(); this.render(); }
  }

  /**
   * Marks/unmarks a checkpoint as a timing point: where the game takes sector times and shows the
   * gain/loss on the cars ahead and behind. The start/finish line is always one (see isStartCp), so
   * this is ignored for it.
   */
  setCpTiming(id, on) {
    const cp = this.cps.find(c => c.id === id);
    if (!cp || this.isStartCp(cp)) return;
    cp.timing = !!on;
    this.notify();
    this.render();
  }

  setTrackId(id) { this.trackId = id; this.notify(); }
  setTrackName(name) { this.trackName = name; this.notify(); }
  setManualWidth(w) { this.manualWidth = w || null; this.notify(); this.render(); }
  setManualHeight(h) { this.manualHeight = h || null; this.notify(); this.render(); }
  // Caller (Vue view) resolves the URL — the engine stays host-agnostic.
  setBackgroundImageUrl(url) {
    this.bgImage = null;
    if (!url) { this.render(); return; }
    const img = new Image();
    img.onload = () => { this.bgImage = img; this.render(); };
    img.onerror = () => { this.bgImage = null; this.render(); };
    img.src = url;
  }

  newTrack() {
    this.segs = []; this.roadAreas = []; this.terrainAreas = []; this.cps = []; this.sg = null; this.pit = null; this.obstacles = [];
    this.nextSeg = 1; this.nextArea = 1; this.nextTerrain = 1; this.nextCp = 0; this.nextObs = 1;
    this.draft = null;
    this.draftKind = 'poly';
    this.extend = null;
    this.hudOverride = null;
    this.selNode = null;
    this.guides = { x: null, y: null };
    this.trackId = 'track_custom';
    this.trackName = 'Custom Track';
    this.manualWidth = null;
    this.manualHeight = null;
    this.bgImage = null;
    this.setSelected(null);
    this.notify();
    this.render();
  }

  fitView() {
    if (!this.canvas?.width || !this.canvas?.height) return;
    const pts = [
      ...this.segs.flatMap(s => [{ x: s.x1, y: s.y1 }, { x: s.x2, y: s.y2 }]),
      ...this.roadAreas.flatMap(a => a.points ?? []),
      ...this.terrainAreas.flatMap(a => a.points ?? []),
      ...this.cps.flatMap(c => [{ x: c.x1, y: c.y1 }, { x: c.x2, y: c.y2 }]),
      ...this.obstacles.flatMap(o => this.obstaclePoints(o)),
      ...(this.sg ? [{ x: this.sg.x1, y: this.sg.y1 }, { x: this.sg.x2, y: this.sg.y2 }] : []),
      ...(this.pit ? [{ x: this.pit.x1, y: this.pit.y1 }, { x: this.pit.x2, y: this.pit.y2 }] : [])
    ];
    if (!pts.length) { this.panX = -2000; this.panY = -1500; this.zoom = 0.15; this.render(); return; }

    const minX = Math.min(...pts.map(p => p.x)), maxX = Math.max(...pts.map(p => p.x));
    const minY = Math.min(...pts.map(p => p.y)), maxY = Math.max(...pts.map(p => p.y));
    const pad = 80;
    this.zoom = Math.min(
      (this.canvas.width - pad * 2) / Math.max(maxX - minX, 1),
      (this.canvas.height - pad * 2) / Math.max(maxY - minY, 1)
    );
    this.panX = minX - pad / this.zoom;
    this.panY = minY - pad / this.zoom;
    this.render();
  }

  /**
   * World bounding box of everything the game draws for this track: the road with its own WIDTH (so
   * its edges are in and not just its centre line), the road areas, the checkpoints, the grid, the pit
   * box, and every object with its own thickness. This is what the game area has to hold, so it is the
   * one measure behind both the dashed box and the automatic size of an export.
   */
  contentBBox() {
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    const add = (x, y, half = 0) => {
      if (x - half < minX) minX = x - half;
      if (x + half > maxX) maxX = x + half;
      if (y - half < minY) minY = y - half;
      if (y + half > maxY) maxY = y + half;
    };

    // Bands come out of roadSamples with a width on every point; an area is a filled ring, i.e. its
    // outline IS its edge.
    for (const run of this.roadSamples()) {
      for (const p of run.points) add(p.x, p.y, run.band ? (p.w ?? 0) / 2 : 0);
    }
    for (const c of this.cps) { add(c.x1, c.y1); add(c.x2, c.y2); }
    for (const area of this.terrainAreas) {
      for (const p of this.shapePoints(area, true)) add(p.x, p.y);
    }
    for (const o of this.obstacles) {
      const half = o.type === 'wall' || o.type === 'poly' ? (o.thickness ?? 0) / 2 : 0;
      // A polygon is measured on its flattened curve: its nodes alone would leave a bulging edge out.
      for (const p of (o.type === 'poly' ? this.shapePoints(o) : this.magnetPoints(o))) add(p.x, p.y, half);
    }
    if (this.sg) { add(this.sg.x1, this.sg.y1); add(this.sg.x2, this.sg.y2); }
    if (this.pit) { add(this.pit.x1, this.pit.y1); add(this.pit.x2, this.pit.y2); }
    return minX === Infinity ? null : { minX, minY, maxX, maxY };
  }

  /**
   * What fitting the game area WOULD do, without touching the track: the size the area needs, and how
   * far the track has to move first (`dx`, `dy`) for everything to land inside it.
   *
   * The area is the rectangle the game keeps the painted surface in (the dashed box this editor draws,
   * a snapshot of it goes to the preview): it starts at the world ORIGIN and goes `width` by `height`
   * from there. A piece drawn to the LEFT of or ABOVE that corner is therefore outside it whatever the
   * two numbers say, and moving the track is the only way to bring it in — the whole geometry goes by
   * a margin minus its own minX. A track already in the positive quadrant is left where it is.
   */
  gameAreaPlan() {
    const box = this.contentBBox();
    if (!box) return null;
    const dx = box.minX < 0 ? Math.round(AREA_MARGIN - box.minX) : 0;
    const dy = box.minY < 0 ? Math.round(AREA_MARGIN - box.minY) : 0;
    return {
      dx, dy,
      width: Math.round(box.maxX + dx + AREA_MARGIN),
      height: Math.round(box.maxY + dy + AREA_MARGIN),
    };
  }

  /**
   * Recomputes the game area (width/height, i.e. the two fields of the Track Info panel) so that the
   * whole track is inside it, moving the track when it has to (see gameAreaPlan). Returns what was
   * written, or null on an empty track.
   */
  fitGameArea() {
    const plan = this.gameAreaPlan();
    if (!plan) return null;
    if (plan.dx || plan.dy) this.translateAll(plan.dx, plan.dy);
    this.manualWidth = plan.width;
    this.manualHeight = plan.height;
    this.notify();
    this.render();
    return plan;
  }

  /**
   * Moves every world coordinate the track holds by (dx, dy): the geometry with its Bezier handles,
   * the objects, and the shapes that are not in the track yet but already have a place on the map — an
   * in-progress draft, the first click of a two-click placement, and the clipboard (a copy taken
   * before the move would otherwise paste back outside the area).
   */
  translateAll(dx, dy) {
    const moveLine = (o) => {
      o.x1 += dx; o.y1 += dy; o.x2 += dx; o.y2 += dy;
      if (o.cx != null) { o.cx += dx; o.cy += dy; }
    };
    for (const s of this.segs) moveLine(s);
    for (const c of this.cps) moveLine(c);
    if (this.sg) moveLine(this.sg);
    if (this.pit) moveLine(this.pit);
    for (const area of this.roadAreas) area.points = (area.points ?? []).map(p => this.shiftNode(p, dx, dy));
    for (const area of this.terrainAreas) area.points = (area.points ?? []).map(p => this.shiftNode(p, dx, dy));
    for (const o of this.obstacles) {
      if (o.type === 'wall') moveLine(o);
      else if (o.type === 'poly') o.points = (o.points ?? []).map(p => this.shiftNode(p, dx, dy));
      else { o.x += dx; o.y += dy; }
    }
    if (this.draft) this.draft = this.draft.map(p => ({ x: p.x + dx, y: p.y + dy }));
    if (this.addFirst) { this.addFirst.x += dx; this.addFirst.y += dy; }
    if (this.clipboard) {
      const data = this.clipboard.data;
      if (data.points) data.points = data.points.map(p => this.shiftNode(p, dx, dy));
      else if (data.x1 != null) moveLine(data);
      else { data.x += dx; data.y += dy; }
    }
  }

  // ── Import / Export ─────────────────────────────────────────
  /**
   * Holds the editor still while something that must not be disturbed is going on. An export paints the
   * picture, then awaits the encode, then writes the JSON: an edit in between would be stamped and written
   * without being in the picture. Pointer events are the view's business (it puts an overlay over the whole
   * editor); this is what stops the keys.
   */
  setBusy(busy) { this.busy = !!busy; }

  /** A node as the editor keeps it: x/y plus the two Bezier handles when they exist. */
  importNode(n) {
    const node = { x: n.x, y: n.y };
    if (n.hIn) node.hIn = { x: n.hIn.x, y: n.hIn.y };
    if (n.hOut) node.hOut = { x: n.hOut.x, y: n.hOut.y };
    return node;
  }

  exportNode(n) {
    return {
      x: Math.round(n.x), y: Math.round(n.y),
      ...(n.hIn && { hIn: { x: Math.round(n.hIn.x), y: Math.round(n.hIn.y) } }),
      ...(n.hOut && { hOut: { x: Math.round(n.hOut.x), y: Math.round(n.hOut.y) } }),
    };
  }

  importTrack(data) {
    const track = fromTrackFile(data);
    this.segs = track.segs.map(seg => ({ ...seg }));
    this.nextSeg = this.segs.length + 1;
    this.cps = [];
    // A road area: the free road shape, a chain of nodes with their Bezier handles. A plain polygon
    // needs no handles at all, so `points` are the nodes and the handles are optional on them (see
    // trackGeometry.edgeControls: no handle on either side of an edge IS the straight line).
    this.roadAreas = track.areas.map((area, i) => ({
      id: i + 1,
      closed: true,
      ...(area.pit === true && { pit: true }),
      ...(area.surface && { surface: area.surface }),
      points: area.points.map(n => this.importNode(n)),
    }));
    this.nextArea = this.roadAreas.length + 1;

    // Terrain areas: closed ground figures, each carrying a terrain name. Same shape as an area node
    // list (a chain with optional Bezier handles); `nodes` is kept only when there is a curve to lose.
    this.terrainAreas = (Array.isArray(data.terrainAreas) ? data.terrainAreas : []).map((a, i) => ({
      id: i + 1,
      closed: true,
      terrain: terrainOf(a),
      points: ((Array.isArray(a.nodes) && a.nodes.length >= 3) ? a.nodes : (a.points ?? [])).map(n => this.importNode(n)),
    })).filter(a => a.points.length >= 3);
    this.nextTerrain = this.terrainAreas.length + 1;

    if (Array.isArray(data.checkpoints)) {
      this.cps = data.checkpoints.map(c => ({ id: c.id, x1: c.x1, y1: c.y1, x2: c.x2, y2: c.y2, ...(c.timing && { timing: true }) }));
      this.nextCp = this.cps.length ? Math.max(...this.cps.map(c => c.id)) + 1 : 0;
    } else {
      this.nextCp = 0;
    }

    this.sg = data.grid
      ? { x1: data.grid.x1, y1: data.grid.y1,
          x2: data.grid.x2, y2: data.grid.y2,
          angle: gridFacing(data.grid),
          staggerPx: data.grid.staggerPx ?? 20,
          flip: !!data.grid.flip }
      : null;

    this.pit = data.pitBox
      ? { x1: data.pitBox.x1, y1: data.pitBox.y1, x2: data.pitBox.x2, y2: data.pitBox.y2,
          crewSide: PIT_CREW_SIDES.includes(data.pitBox.crewSide) ? data.pitBox.crewSide : 'auto',
          crewOffset: Number.isFinite(Number(data.pitBox.crewOffset))
            ? clampCrewOffset(data.pitBox.crewOffset)
            : DEFAULT_CREW_OFFSET,
          direction: knownPitDirection(data.pitBox.direction) }
      : null;

    this.obstacles = Array.isArray(data.obstacles) ? data.obstacles.map((o, i) => ({
      id: i + 1,
      type: o.type,
      restitution: o.restitution ?? this.defObsRestitution,
      friction: o.friction ?? this.defObsFriction,
      // The material is checked against the TYPE: a file that hands a wall a stand's material gets the
      // default instead of a name its type can never be made of (see materialsForType).
      material: knownMaterial(o.material, o.type),
      ...(o.type === 'circle' ? { x: o.x, y: o.y, radius: o.radius }
        : o.type === 'rect' ? {
            x: o.x, y: o.y, width: o.width, height: o.height, angle: o.angle ?? 0,
            variant: Number.isInteger(o.variant) ? Math.max(0, o.variant) : 0 }
        : o.type === 'poly' ? {
            points: (Array.isArray(o.nodes) && o.nodes.length >= 2 ? o.nodes : (o.points ?? [])).map(n => this.importNode(n)),
            thickness: o.thickness ?? this.defPolyThickness }
        : { x1: o.x1, y1: o.y1, x2: o.x2, y2: o.y2, thickness: o.thickness ?? this.defObsThickness })
    })) : [];
    this.nextObs = this.obstacles.length ? Math.max(...this.obstacles.map(o => o.id)) + 1 : 1;

    this.trackId = data.id || 'track_custom';
    this.trackName = data.name || 'Custom Track';
    this.manualWidth = data.width ?? null;
    this.manualHeight = data.height ?? null;

    this.setSelected(null);
    this.notify();
    this.fitView();
  }

  modalWidth(segments) {
    if (!segments.length) return 180;
    const freq = {};
    for (const s of segments) freq[s.width] = (freq[s.width] || 0) + 1;
    return parseInt(Object.entries(freq).sort((a, b) => b[1] - a[1])[0][0]);
  }

  /**
   * Start pose as the export writes it (rounded): where the race starts and which way it's driven.
   * Also the editor's stand-in for "which way cars face in the pit box" (see pitCrewSide).
   *
   * With no starting grid line the start falls back to the FIRST road piece by path order — segment
   * or area — so a track drawn as road areas starts on its road instead of at the map's corner.
   */
  startPose() {
    let startPosition = { x: 0, y: 0 }, startAngle = 0;
    if (this.sg) {
      startPosition = { x: Math.round((this.sg.x1 + this.sg.x2) / 2), y: Math.round((this.sg.y1 + this.sg.y2) / 2) };
      startAngle = Math.round(normAngle(this.sg.angle) * 1000) / 1000;
      return { startPosition, startAngle };
    }

    const first = this.segs.length
      ? { kind: 'seg', obj: this.segs[0] }
      : (this.roadAreas.length ? { kind: 'area', obj: this.roadAreas[0] } : null);

    if (first?.kind === 'seg') {
      const s = first.obj;
      startPosition = { x: Math.round(s.x1), y: Math.round(s.y1) };
      startAngle = Math.round(Math.atan2(s.y2 - s.y1, s.x2 - s.x1) * 1000) / 1000;
    } else if (first?.kind === 'area') {
      const points = first.obj.points ?? [];
      const a = points[0], b = points[1] ?? points[0];
      if (a) {
        startPosition = { x: Math.round(a.x), y: Math.round(a.y) };
        startAngle = Math.round(Math.atan2(b.y - a.y, b.x - a.x) * 1000) / 1000;
      }
    }
    return { startPosition, startAngle };
  }

  exportTrack() {
    const id = this.trackId || 'track_custom';
    const name = this.trackName || 'Custom Track';

    const mainSegs = this.segs.filter(s => (s.lane ?? 'main') === 'main');
    const roadWidth = this.modalWidth(mainSegs.length ? mainSegs : this.segs);
    const pieces = buildPieces({ segs: this.segs, areas: this.roadAreas, roadWidth });

    // An automatic size is the far corner of everything the track holds plus the margin: the area
    // starts at the world ORIGIN (see gameAreaPlan), so it does NOT extend a margin to the LEFT of the
    // content — that would make the rectangle cover a stretch of world that the game never uses, and
    // leave the content itself sitting outside whatever the number says.
    const box = this.contentBBox() ?? { maxX: 1000, maxY: 1000 };

    const { startPosition, startAngle } = this.startPose();

    // The starting grid is ONE field now (`grid`): the line plus `angle`. A drawn line is used as is;
    // a track with no line still has a start pose (see startPose), and the game reads the pose back
    // from `grid` alone, so one is synthesized around it rather than lost.
    const grid = this.sg ?? gridForPose(startPosition, startAngle, roadWidth);

    const manualW = this.manualWidth || 0;
    const manualH = this.manualHeight || 0;

    return {
      id, name,
      width: manualW || Math.round(box.maxX + AREA_MARGIN),
      height: manualH || Math.round(box.maxY + AREA_MARGIN),
      roadWidth,
      grid: {
        x1: Math.round(grid.x1), y1: Math.round(grid.y1),
        x2: Math.round(grid.x2), y2: Math.round(grid.y2),
        angle: startAngle,
        staggerPx: grid.staggerPx ?? 20,
        flip: !!grid.flip,
      },
      checkpoints: this.cps.map(c => ({
        id: c.id,
        x1: Math.round(c.x1), y1: Math.round(c.y1),
        x2: Math.round(c.x2), y2: Math.round(c.y2),
        ...(c.timing && { timing: true })
      })),
      pieces,
      ...(this.terrainAreas.length && { terrainAreas: this.terrainAreas.map(a => {
        const ring = this.shapePoints(a, true);
        // `shapePoints` closes the ring back onto the first node: the duplicate goes away, because a
        // closed polygon is implicit everywhere downstream (the game server and the painter both
        // close it themselves).
        if (ring.length > 1) {
          const first = ring[0], last = ring[ring.length - 1];
          if (Math.hypot(last.x - first.x, last.y - first.y) < 0.5) ring.pop();
        }
        return {
          id: a.id,
          ...(terrainOf(a) !== DEFAULT_TERRAIN && { terrain: terrainOf(a) }),
          points: ring.map(p => ({ x: Math.round(p.x), y: Math.round(p.y) })),
          ...((a.points ?? []).some(n => n.hIn || n.hOut) && { nodes: a.points.map(n => this.exportNode(n)) }),
        };
      }) }),
      ...(this.pit && { pitBox: {
        x1: Math.round(Math.min(this.pit.x1, this.pit.x2)),
        y1: Math.round(Math.min(this.pit.y1, this.pit.y2)),
        x2: Math.round(Math.max(this.pit.x1, this.pit.x2)),
        y2: Math.round(Math.max(this.pit.y1, this.pit.y2)),
        // Left out when unset: the game falls back to its own defaults for old tracks.
        ...(this.pit.crewSide && this.pit.crewSide !== 'auto' && { crewSide: this.pit.crewSide }),
        ...(this.pit.crewOffset !== DEFAULT_CREW_OFFSET && { crewOffset: this.pit.crewOffset }),
        // Same: 'auto' is what the game does with no field at all.
        ...(typeof this.pit.direction === 'number'
          ? { direction: Math.round(this.pit.direction * 1000) / 1000 }
          : (this.pit.direction && this.pit.direction !== 'auto' && { direction: this.pit.direction }))
      }}),
      ...(this.obstacles.length && { obstacles: this.obstacles.map(o => ({
        id: o.id, type: o.type, restitution: o.restitution, friction: o.friction, material: o.material,
        ...(o.type === 'circle' ? { x: Math.round(o.x), y: Math.round(o.y), radius: Math.round(o.radius) }
          : o.type === 'rect' ? {
            x: Math.round(o.x), y: Math.round(o.y),
            width: Math.round(o.width), height: Math.round(o.height),
            angle: Math.round((o.angle ?? 0) * 1000) / 1000,
            // Which sprite a stand uses: 0 is the game's default, so it is left out like every other
            // default (an old file has no field either, and means the same thing).
            ...(o.variant ? { variant: o.variant } : {})
          }
          : o.type === 'poly' ? {
            thickness: o.thickness,
            // `points` is the sampled polyline the game consumes (curves flattened); `nodes` keeps
            // the Bezier handles for the editor, and only when there is a curve to lose.
            points: this.shapePoints(o, false).map(p => ({ x: Math.round(p.x), y: Math.round(p.y) })),
            ...((o.points ?? []).some(n => n.hIn || n.hOut) && { nodes: o.points.map(n => this.exportNode(n)) })
          }
          : {
            x1: Math.round(o.x1), y1: Math.round(o.y1),
            x2: Math.round(o.x2), y2: Math.round(o.y2),
            thickness: o.thickness
          })
      })) })
    };
  }

  /**
   * The track's own look as ONE picture, at the resolution the game would have baked it at — the very
   * rectangle the game stretches a background image over. It is the game's
   * own painter that draws it, so while the geometry stands still the picture is exactly what the game would
   * have painted itself, and there is nothing left for the game to paint.
   *
   * `terrainImages` are the ground textures it needs — the base grass plus one per terrain figure — keyed by
   * terrain name, the same the game's painter takes in `opts`: the editor's own canvas never draws ground, so
   * those files are fetched only when a picture is exported (see pictureTerrainSources).
   */
  exportPicture(terrainImages = {}) {
    if (!this.createCanvas) return null;
    const track = this.exportTrack();
    if (!(track.width > 0) || !(track.height > 0)) return null;
    // No floor at one here, unlike the game's own bake: a picture is a file AND a texture, so a track bigger
    // than the ceilings has to come out smaller than one pixel per world unit rather than impossible to load.
    const scale = scaleWithinCeilings(track);
    return {
      track, scale,
      canvas: createTrackCanvas(track, {
        createCanvas: this.createCanvas,
        images: Object.fromEntries(this.materialImages),
        terrainImages,
        scale,
      }),
    };
  }

  /** The ground textures `exportPicture` needs, as `{ terrain, src }`: what the caller has to fetch. */
  pictureTerrainSources() {
    return terrainImages(this.exportTrack());
  }

  /**
   * The sprites the picture will draw, as srcs the caller has to fetch. `materialImages` on the engine is
   * only what the editor has LOOKED at so far (it asks for a sprite when it is about to draw it on
   * screen), and a sprite that is missing is painted as nothing: that is how the vegetation — which is
   * never on the editor's canvas — failed to reach every exported picture.
   */
  pictureImageSources() {
    return materialImages(this.exportTrack()).map(({ src }) => src);
  }

  /**
   * The track file of a track whose look is the exported picture: it names the picture and stamps it with the
   * geometry it was painted from. `tiles` is the `backgroundTiles` manifest when the export also wrote a
   * tile pyramid (see tileExport.js): the game draws from that and never downloads the picture. A pyramid
   * export also writes the picker's thumbnail beside it, in `preview/<id>.webp`.
   */
  exportTrackFile(pictureFile, tiles = null) {
    const track = this.exportTrack();
    return {
      ...track,
      backgroundImage: pictureFile,
      backgroundStamp: trackExportStamp(track),
      ...(tiles ? { backgroundTiles: tiles } : {}),
    };
  }

  /**
   * The track as ONE picture PER LAYER, each on its own transparent canvas — the road, the objects and
   * the lines — so they can be finished one at a time and stacked back into the single flat picture
   * they were split from.
   *
   * Every layer uses the same frame and the same scale: the canvas IS the game area — the same `width`
   * by `height` an export writes, world (0, 0) at pixel (0, 0), one world unit per pixel — which is
   * what lets them be overlaid exactly, and is also the rectangle the game stretches a hand-painted
   * background image over. An area too big for a canvas is scaled down (all of them by the same
   * factor) instead of coming out blank.
   */
  exportImages() {
    if (!this.createCanvas) return [];
    const area = this.getBoundsRect();
    if (!area) return [];

    const scale = Math.min(1, LAYER_MAX_PX / Math.max(area.w, area.h));
    const px = (v) => Math.max(1, Math.round(v * scale));
    const id = this.trackId || 'track_custom';
    // In this order, which is also the order to stack them back in: the lines end up over the road and
    // the objects they mark places on.
    const layers = [
      { name: 'track', paint: this.paintRoadLayer },
      { name: 'objects', paint: this.paintObjectLayer },
      { name: 'lines', paint: this.paintLineLayer },
    ];

    return layers.map(({ name, paint }) => {
      const canvas = this.createCanvas(px(area.w), px(area.h));
      const ctx = canvas.getContext('2d');
      ctx.scale(scale, scale);
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      paint.call(this, ctx);
      return { id, name, width: px(area.w), height: px(area.h), scale, canvas };
    });
  }

  /** The road alone: the corridor the game paints (and drives on), flat and untextured. */
  paintRoadLayer(ctx) {
    ctx.fillStyle = LAYER_COLORS.track;
    ctx.strokeStyle = LAYER_COLORS.track;
    for (const run of this.roadSamples()) {
      const pts = run.points;
      // A road area is a filled ring — its outline IS its edge — while a band is a capsule chain. The
      // widths are the ones the game paints the road with, so the two surfaces can only be the same.
      if (!run.band) {
        ctx.beginPath();
        pts.forEach((p, i) => (i ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y)));
        ctx.closePath();
        ctx.fill();
        continue;
      }
      this.strokeCapsule(ctx, pts, (p) => p.w ?? 0, run.closed);
    }
  }

  /** The objects alone (walls, circles, blocks, polygons): the shapes the game collides with. */
  paintObjectLayer(ctx) {
    ctx.fillStyle = LAYER_COLORS.objects;
    ctx.strokeStyle = LAYER_COLORS.objects;
    for (const o of this.obstacles) {
      if (o.type === 'circle') {
        ctx.beginPath();
        ctx.arc(o.x, o.y, o.radius, 0, Math.PI * 2);
        ctx.fill();
      } else if (o.type === 'rect') {
        const corners = this.obstaclePoints(o);
        ctx.beginPath();
        corners.forEach((p, i) => (i ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y)));
        ctx.closePath();
        ctx.fill();
      } else if (o.type === 'poly') {
        this.strokeCapsule(ctx, this.shapePoints(o, !!o.closed), () => o.thickness ?? 0, !!o.closed);
      } else {
        this.strokeCapsule(ctx, [{ x: o.x1, y: o.y1 }, { x: o.x2, y: o.y2 }], () => o.thickness ?? 0);
      }
    }
  }

  /** The lines alone: the starting grid and the finish line (the first checkpoint). */
  paintLineLayer(ctx) {
    ctx.strokeStyle = LAYER_COLORS.lines;
    ctx.lineWidth = LAYER_LINE_WIDTH;
    ctx.lineCap = 'butt'; // a marking across the road, so it must not bulge past its own ends
    const startLine = this.cps.find((c) => this.isStartCp(c));
    for (const line of [this.sg, startLine]) {
      if (!line) continue;
      ctx.beginPath();
      ctx.moveTo(line.x1, line.y1);
      ctx.lineTo(line.x2, line.y2);
      ctx.stroke();
    }
  }

  /**
   * A polyline as a capsule chain: every pair of points stroked at their widths, plus a disc on every
   * point. Round caps and joins on a single stroke are not enough — one stroke takes ONE lineWidth, and
   * the road narrows or opens up along the way (which is exactly what the discs at the nodes are for).
   */
  strokeCapsule(ctx, pts, widthAt, closed = false) {
    if (pts.length < 2) return;
    const last = closed ? pts.length : pts.length - 1;
    for (let i = 0; i < last; i++) {
      const a = pts[i], b = pts[(i + 1) % pts.length];
      ctx.lineWidth = (widthAt(a) + widthAt(b)) / 2;
      ctx.beginPath();
      ctx.moveTo(a.x, a.y);
      ctx.lineTo(b.x, b.y);
      ctx.stroke();
    }
    for (const p of pts) {
      ctx.beginPath();
      ctx.arc(p.x, p.y, widthAt(p) / 2, 0, Math.PI * 2);
      ctx.fill();
    }
  }
}
